// src/pages/Timesheets.tsx — Weekly timesheet entry, OT calc, approval, payroll export
import { useState, useEffect } from 'react'
import { useAuthStore } from '../stores/auth.store'
import { MOCK_TIMESHEETS, MOCK_EMPLOYEES, WEEK_DATES, DAY_LABELS, calcHours, COST_CODES, PAY_RATES,
  setNightShiftLoading, getNightShiftLoading, getPayPeriod,
  getOTThreshold, getWeeklyHoursCap, getWeeklyAllocatedHours } from '../utils/mockDataExtended'
import { useQuery } from '@tanstack/react-query'
import { fetchTimesheets, updateTimesheetStatus } from '../services/timesheetsService'
import { getLeaveBalance, formatLeaveBalance, accrueToil } from '../utils/mockDataLeave'
import { getRosterAllocations } from '../pages/Roster'
import { getDocumentBranding, buildDocumentHeader, buildDocumentFooter } from '../utils/documentBranding'
import { getPayRates, getEffectiveRate } from '../pages/Settings'
import { MOCK_ROSTER_NOTIFICATIONS } from '../utils/mockDataMobile'
import { MOCK_JOBS } from '../utils/mockData'
import { MOCK_JOB_PACKAGES } from '../utils/mockDataWorkInstructions'
import { fireTimesheetApprovedAlert } from '../services/remainingModuleConnectors'
import { formatCurrency, formatDate } from '../utils/formatters'
import {
  ClipboardList, ChevronLeft, ChevronRight, CheckCircle,
  AlertTriangle, Clock, X, Download, Users, DollarSign,
  ChevronDown, Pencil, Smartphone, UserCheck, RotateCcw,
} from 'lucide-react'


type TimesheetStatus = 'DRAFT' | 'SUBMITTED' | 'APPROVED' | 'REJECTED' | 'PROCESSED'

interface Entry {
  id: string
  date: string
  dayIndex: number
  start: string
  end: string
  break: number
  jobId: string | null
  jobNumber: string | null
  costCode: string
  activity: string
  fatigueOk: boolean
  // Phase 4: task-level linkage for actuals feedback loop
  wiId?: string | null            // WorkInstruction.id — enables per-SOP actuals capture
  wiReference?: string | null     // display name for the linked WI
  configuredTaskId?: string | null // ConfiguredTask.id (optional, more granular)
  adjustedByPM?: boolean
  pmAdjustedAt?: string
  shiftType?: 'DAY' | 'NIGHT' | 'HALF'
  // Phase 1 additions
  entryType?: 'SITE_WORK' | 'ANNUAL_LEAVE' | 'PERSONAL_LEAVE' | 'TOIL' | 'WORKERS_COMP' | 'TAFE_ATTENDANCE' | 'PUBLIC_HOLIDAY' | 'UNPAID_LEAVE'
  wcClaimRef?: string | null
  tafeBlockRef?: string | null
}

interface TS {
  id: string
  employeeId: string
  employeeName: string
  weekStarting: string
  status: TimesheetStatus
  submittedAt: string | null
  approvedById: string | null
  approvedAt: string | null
  rejectedReason?: string
  entries: Entry[]
}

const STATUS_STYLE: Record<string, { bg: string; text: string; label: string }> = {
  DRAFT:     { bg: 'bg-gray-100',   text: 'text-gray-600',  label: 'Draft' },
  SUBMITTED: { bg: 'bg-blue-100',   text: 'text-blue-700',  label: 'Submitted' },
  APPROVED:  { bg: 'bg-green-100',  text: 'text-green-700', label: 'Approved' },
  REJECTED:  { bg: 'bg-red-100',    text: 'text-red-700',   label: 'Rejected' },
  PROCESSED: { bg: 'bg-purple-100', text: 'text-purple-700',label: 'Processed' },
}

function shiftHours(e: Entry) { return calcHours(e.start, e.end, e.break, e.dayIndex, e.shiftType ?? 'DAY') }

function totalHours(ts: TS) {
  return ts.entries.reduce((acc, e) => {
    const h = shiftHours(e)
    return {
      ordinary:    acc.ordinary    + h.ordinary,
      overtime:    acc.overtime    + h.overtime,
      doubleTime:  acc.doubleTime  + h.doubleTime,
      nightLoading:acc.nightLoading + (h.nightLoading ?? 0),
      total:       acc.total       + h.total,
    }
  }, { ordinary: 0, overtime: 0, doubleTime: 0, nightLoading: 0, total: 0 })
}

function estimatedGross(ts: TS, payRate: number) {
  const h = totalHours(ts)
  // nightLoading is ordinary hours × loading % — multiply by base rate for dollar value
  return h.ordinary * payRate
       + h.overtime  * payRate * 1.5
       + h.doubleTime * payRate * 2
       + h.nightLoading * payRate
}

// ── Entry cell (one day) ──────────────────────────────────────────
function EntryCell({ entry, editable, onUpdate, mobileHours, isPMEdit }: {
  entry: Entry; editable: boolean; isPMEdit?: boolean
  mobileHours?: number | null   // pre-populated from mobile app attendance
  onUpdate: (id: string, field: string, val: string | number | null) => void
}) {
  const hrs = shiftHours(entry)
  const hasEntry = entry.start && entry.end

  // Convert mobile hours to suggested start/end times
  const prefillFromMobile = () => {
    if (!mobileHours) return
    // Standard start 07:00 + mobileHours
    const startH = 7, startM = 0
    const endTotalMins = startH * 60 + startM + Math.round(mobileHours * 60) + 30 // +30 min break
    const endH = Math.floor(endTotalMins / 60)
    const endM = endTotalMins % 60
    onUpdate(entry.id, 'start', `${String(startH).padStart(2,'0')}:${String(startM).padStart(2,'0')}`)
    onUpdate(entry.id, 'end',   `${String(Math.min(endH, 23)).padStart(2,'0')}:${String(endM).padStart(2,'0')}`)
  }

  return (
    <td className="border-r last:border-r-0 border-gray-100 p-0 align-top">
      <div className={`p-2 min-h-[90px] ${entry.adjustedByPM ? 'bg-purple-50' : ''}`}>
        {(editable || isPMEdit) ? (
          <div className="space-y-1">
            <div className="flex gap-1">
              <input type="time" className="text-xs border border-gray-200 rounded px-1 py-0.5 w-full" value={entry.start} onChange={e => onUpdate(entry.id, 'start', e.target.value)} />
              <input type="time" className="text-xs border border-gray-200 rounded px-1 py-0.5 w-full" value={entry.end}   onChange={e => onUpdate(entry.id, 'end',   e.target.value)} />
            </div>
            {hasEntry && (
              <>
                <select className="text-xs border border-gray-200 rounded px-1 py-0.5 w-full"
                  value={entry.jobId ?? ''} onChange={e => onUpdate(entry.id, 'jobId', e.target.value)}>
                  <option value="">— Select job —</option>
                  {MOCK_JOBS.map(j => <option key={j.id} value={j.id}>{j.jobNumber}</option>)}
                </select>
                <select className="text-xs border border-gray-200 rounded px-1 py-0.5 w-full"
                  value={entry.costCode} onChange={e => onUpdate(entry.id, 'costCode', e.target.value)}>
                  <option value="">— Cost code —</option>
                  {COST_CODES.map(c => <option key={c.code} value={c.code}>{c.code}</option>)}
                </select>
                {/* Phase 4: optional WI linkage for actuals capture */}
                <select
                  title="Link to Work Instruction (optional — enables actuals vs estimate tracking)"
                  className="text-xs border border-indigo-200 rounded px-1 py-0.5 w-full bg-indigo-50"
                  value={entry.wiId ?? ''}
                  onChange={e => {
                    const wi = MOCK_JOB_PACKAGES.find(w => w.id === e.target.value)
                    onUpdate(entry.id, 'wiId', e.target.value || null)
                    onUpdate(entry.id, 'wiReference', wi?.reference ?? null)
                  }}>
                  <option value="">— Link to WI (optional) —</option>
                  {MOCK_JOB_PACKAGES.filter(w => !entry.jobId || w.applicableJobIds?.includes(entry.jobId)).map(w => (
                    <option key={w.id} value={w.id}>{w.reference} — {w.title.slice(0, 30)}</option>
                  ))}
                </select>
              </>
            )}
          </div>
        ) : (
          hasEntry ? (
            <div>
              <div className="text-xs font-mono text-gray-600">{entry.start}–{entry.end}</div>
              {entry.jobNumber && <div className="text-xs text-blue-600 mt-0.5">{entry.jobNumber}</div>}
              {entry.costCode  && <div className="text-xs text-gray-400">{entry.costCode}</div>}
              {entry.wiReference && <div className="text-xs text-indigo-500 mt-0.5 flex items-center gap-0.5">⚡{entry.wiReference}</div>}
            </div>
          ) : (
            <div className="text-xs text-gray-300 text-center mt-4">—</div>
          )
        )}
        {hasEntry && (
          <div className="mt-1 pt-1 border-t border-gray-100 text-xs space-y-0.5">
            {hrs.ordinary   > 0 && <div className="text-gray-600">{hrs.ordinary.toFixed(1)}h ord</div>}
            {hrs.overtime     > 0 && <div className="text-amber-600">{hrs.overtime.toFixed(1)}h OT</div>}
            {hrs.nightLoading > 0 && <div className="text-indigo-600 text-[10px]">{getNightShiftLoading()}% night loading</div>}
            {hrs.doubleTime > 0 && <div className="text-red-600">{hrs.doubleTime.toFixed(1)}h DT</div>}
          </div>
        )}
        {/* Mobile hours prefill hint */}
        {!hasEntry && mobileHours != null && mobileHours > 0 && (editable || isPMEdit) && (
          <button onClick={prefillFromMobile}
            className="mt-1 text-[10px] bg-teal-50 text-teal-700 border border-teal-200 rounded px-1.5 py-0.5 flex items-center gap-0.5 hover:bg-teal-100 transition-colors">
            <Smartphone className="w-2.5 h-2.5" />{mobileHours.toFixed(1)}h from mobile
          </button>
        )}
        {/* PM adjustment badge */}
        {entry.adjustedByPM && (
          <div className="mt-1 text-[10px] text-purple-600 flex items-center gap-0.5">
            <Pencil className="w-2.5 h-2.5" />Adj. by PM
          </div>
        )}
      </div>
    </td>
  )
}

// ── My timesheet (edit + submit) ──────────────────────────────────
function MyTimesheet({ ts, payRate, onSubmit, onUpdate, isPMEdit, viewingEmployeeName }: {
  ts: TS; payRate: number; isPMEdit?: boolean; viewingEmployeeName?: string
  onSubmit: () => void; onUpdate: (id: string, field: string, val: any) => void
}) {
  const editable  = ts.status === 'DRAFT'
  const isLocked  = ts.status === 'PROCESSED'
  const totals   = totalHours(ts)
  const gross    = estimatedGross(ts, payRate)
  const ss = STATUS_STYLE[ts.status]
  const weekHours = totals.total
  const fatigueWarn = weekHours >= 55
  const fatigueCrit = weekHours >= 60

  return (
    <div className="bg-white border border-gray-200 rounded-xl overflow-hidden">
      {/* PM edit mode banner */}
      {isPMEdit && (
        <div className="bg-purple-50 border-b border-purple-200 px-5 py-2.5 flex items-center gap-2 text-xs text-purple-800">
          <Pencil className="w-3.5 h-3.5 flex-shrink-0" />
          <strong>PM Adjustment Mode</strong> — you are editing {viewingEmployeeName ?? ts.employeeName}'s timesheet.
          All changes are logged. Adjustments are marked on individual entries.
        </div>
      )}

      <div className="px-5 py-4 border-b flex items-center justify-between gap-3 flex-wrap">
        <div className="flex items-center gap-3">
          <h3 className="font-semibold text-gray-900">
            {viewingEmployeeName ?? ts.employeeName} — Week of {formatDate(ts.weekStarting)}
          </h3>
          <span className={`text-xs px-2 py-0.5 rounded-full font-medium ${ss.bg} ${ss.text}`}>{ss.label}</span>
        {isLocked && (
          <span className="text-xs font-medium text-purple-600" title={(ts as any).lockedAt ? `Exported ${new Date((ts as any).lockedAt).toLocaleDateString('en-AU')}` : 'Payroll exported'}>
            🔒 Locked
          </span>
        )}
        </div>
        <div className="flex items-center gap-2">
          {fatigueCrit && <span className="text-xs bg-red-100 text-red-700 px-2 py-0.5 rounded-full flex items-center gap-1"><AlertTriangle className="w-3 h-3" />Fatigue limit reached</span>}
          {fatigueWarn && !fatigueCrit && <span className="text-xs bg-amber-100 text-amber-700 px-2 py-0.5 rounded-full flex items-center gap-1"><Clock className="w-3 h-3" />Approaching fatigue limit</span>}
          {editable && (
            <button onClick={onSubmit} disabled={totals.total === 0} className="btn-primary text-xs">Submit for approval</button>
          )}
        </div>
      </div>

      <div className="overflow-x-auto">
        <table className="w-full border-collapse" style={{ minWidth: 700 }}>
          <thead>
            <tr className="bg-gray-50 border-b border-gray-200">
              <th className="w-16 px-3 py-2 text-left text-xs font-medium text-gray-500">Day</th>
              {WEEK_DATES.map((d, i) => (
                <th key={d} className={`text-xs font-medium text-gray-500 px-2 py-2 text-center border-r last:border-r-0 ${i >= 5 ? 'bg-gray-100' : ''}`}>
                  <div>{DAY_LABELS[i]}</div>
                  <div className="font-normal text-gray-400">{d.slice(8)}/{d.slice(5,7)}</div>
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            <tr className="border-b border-gray-100">
              <td className="px-3 py-1 text-xs text-gray-500 font-medium whitespace-nowrap">Hours</td>
              {ts.entries.map(entry => {
                // Look up mobile hours for this employee + date
                const rn = MOCK_ROSTER_NOTIFICATIONS.find(r =>
                  r.employeeId === ts.employeeId &&
                  r.date === entry.date &&
                  r.hoursWorked != null
                )
                return (
                  <EntryCell key={entry.id} entry={entry} editable={editable}
                    isPMEdit={isPMEdit} mobileHours={rn?.hoursWorked ?? null}
                    onUpdate={onUpdate} />
                )
              })}
            </tr>
          </tbody>
        </table>
      </div>

      {/* Totals */}
      <div className="px-5 py-3 border-t bg-gray-50 flex items-center gap-6 flex-wrap text-sm">
        <div><span className="text-gray-500 text-xs">Ordinary</span><div className="font-semibold">{totals.ordinary.toFixed(1)}h</div></div>
        <div><span className="text-gray-500 text-xs">Overtime (1.5x)</span><div className="font-semibold text-amber-600">{totals.overtime.toFixed(1)}h</div></div>
        <div><span className="text-gray-500 text-xs">Double time (2x)</span><div className="font-semibold text-red-600">{totals.doubleTime.toFixed(1)}h</div></div>
        <div><span className="text-gray-500 text-xs">Total hours</span><div className="font-bold text-gray-900">{totals.total.toFixed(1)}h</div></div>
        <div className="ml-auto"><span className="text-gray-500 text-xs">Est. gross (ex super)</span><div className="font-bold text-gray-900">{formatCurrency(gross)}</div></div>
      </div>
    </div>
  )
}

// ── Team timesheets (approve / reject) ───────────────────────────
function TeamTimesheets({ timesheets, onApprove, onReject, onSelectEmployee, selectedEmployeeId }: {
  timesheets: TS[]
  onApprove: (id: string) => void; onReject: (id: string, reason: string) => void
  onSelectEmployee: (id: string) => void; selectedEmployeeId: string
}) {
  const pending = timesheets.filter(t => t.status === 'SUBMITTED')
  return (
    <div className="space-y-3">
      <div className="flex items-center gap-2 mb-1">
        <h3 className="font-semibold text-gray-900">Team timesheets</h3>
        {pending.length > 0 && <span className="text-xs bg-amber-100 text-amber-700 px-2 py-0.5 rounded-full font-medium">{pending.length} pending approval</span>}
        <span className="text-xs text-gray-400 ml-auto">Click an employee to view &amp; adjust their full timesheet</span>
      </div>
      {timesheets.length === 0
        ? <div className="bg-white border border-gray-200 rounded-xl py-10 text-center text-gray-400 text-sm">No team timesheets this week</div>
        : (
          <div className="bg-white border border-gray-200 rounded-xl divide-y divide-gray-100 overflow-hidden">
            {timesheets.map(ts => {
              const totals  = totalHours(ts)
              const emp     = MOCK_EMPLOYEES.find(e => e.id === ts.employeeId)
              const payRate = getEffectiveRate(emp?.payClassification ?? '', (emp as any)?.isAdultApprentice ?? false)
              const gross   = estimatedGross(ts, payRate)
              const ss      = STATUS_STYLE[ts.status]
              const isSelected = ts.employeeId === selectedEmployeeId
              return (
                <div key={ts.id} className={`flex items-center gap-4 transition-colors cursor-pointer ${isSelected ? 'bg-purple-50 border-l-4 border-purple-500' : 'hover:bg-gray-50 px-5'}`}
                  style={isSelected ? {paddingLeft:'16px'} : {}}
                  onClick={() => onSelectEmployee(ts.employeeId)}>
                  <div className="py-3.5 flex items-center gap-4 flex-1 min-w-0">
                    <div className={`w-8 h-8 rounded-full flex items-center justify-center text-white text-xs font-bold flex-shrink-0 ${isSelected ? 'bg-purple-600' : 'bg-brand-navy'}`}>
                      {ts.employeeName.split(' ').map((n:string) => n[0]).join('')}
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 mb-0.5">
                        <span className="font-medium text-sm text-gray-900">{ts.employeeName}</span>
                        <span className={`text-xs px-1.5 py-0.5 rounded-full font-medium ${ss.bg} ${ss.text}`}>{ss.label}</span>
                        {isSelected && <span className="text-xs bg-purple-100 text-purple-700 px-1.5 py-0.5 rounded-full font-medium flex items-center gap-1"><Pencil className="w-2.5 h-2.5" />Viewing &amp; adjusting</span>}
                      </div>
                      <p className="text-xs text-gray-500">
                      {totals.total.toFixed(1)}h total
                      {' · '}ord {totals.ordinary.toFixed(1)}h
                      {' · '}OT {totals.overtime.toFixed(1)}h
                      {totals.nightLoading > 0 && ` · ${getNightShiftLoading()}% night loading`}
                      {' · '}est. {formatCurrency(gross)}
                    </p>
                    </div>
                  </div>
                  <div className="flex gap-2 pr-4 flex-shrink-0" onClick={e => e.stopPropagation()}>
                    {ts.status === 'SUBMITTED' && (
                      <>
                        <button onClick={() => onApprove(ts.id)} className="text-xs px-3 py-1.5 bg-green-100 text-green-700 rounded-lg font-medium hover:bg-green-200 transition-colors flex items-center gap-1"><CheckCircle className="w-3.5 h-3.5" />Approve</button>
                        <button onClick={() => onReject(ts.id, 'Needs correction')} className="text-xs px-3 py-1.5 bg-red-100 text-red-700 rounded-lg font-medium hover:bg-red-200 transition-colors flex items-center gap-1"><X className="w-3.5 h-3.5" />Reject</button>
                      </>
                    )}
                  </div>
                </div>
              )
            })}
          </div>
        )
      }
    </div>
  )
}

// ── Labour cost by job ────────────────────────────────────────────
function LabourCostSummary({ timesheets }: { timesheets: TS[] }) {
  const byJob: Record<string, { jobNumber: string; hours: number; cost: number }> = {}
  for (const ts of timesheets) {
    const emp = MOCK_EMPLOYEES.find(e => e.id === ts.employeeId)
    const rate = getEffectiveRate(emp?.payClassification ?? '', (emp as any)?.isAdultApprentice ?? false)
    for (const entry of ts.entries) {
      if (!entry.jobId || !entry.jobNumber) continue
      const h = shiftHours(entry)
      if (!byJob[entry.jobId]) byJob[entry.jobId] = { jobNumber: entry.jobNumber, hours: 0, cost: 0 }
      byJob[entry.jobId].hours += h.total
      byJob[entry.jobId].cost += h.ordinary * rate + h.overtime * rate * 1.5 + h.doubleTime * rate * 2
    }
  }
  const rows = Object.values(byJob).sort((a,b) => b.cost - a.cost)
  if (rows.length === 0) return null
  return (
    <div className="bg-white border border-gray-200 rounded-xl overflow-hidden">
      <div className="px-5 py-4 border-b flex items-center gap-2">
        <DollarSign className="w-4 h-4 text-gray-500" />
        <h3 className="font-semibold text-gray-900">Labour cost by job — this week</h3>
      </div>
      <table className="w-full text-sm">
        <thead><tr className="text-xs text-gray-500 border-b bg-gray-50"><th className="px-5 py-2 text-left">Job</th><th className="px-3 py-2 text-right">Hours</th><th className="px-5 py-2 text-right">Est. cost</th></tr></thead>
        <tbody className="divide-y divide-gray-50">
          {rows.map(r => (
            <tr key={r.jobNumber}>
              <td className="px-5 py-3 font-medium">{r.jobNumber}</td>
              <td className="px-3 py-3 text-right tabular-nums text-gray-600">{r.hours.toFixed(1)}h</td>
              <td className="px-5 py-3 text-right tabular-nums font-semibold">{formatCurrency(r.cost)}</td>
            </tr>
          ))}
        </tbody>
        <tfoot className="border-t">
          <tr>
            <td className="px-5 py-2 text-xs text-gray-500">Total (ex super, ex allowances)</td>
            <td className="px-3 py-2 text-right text-xs font-bold">{rows.reduce((s,r) => s+r.hours,0).toFixed(1)}h</td>
            <td className="px-5 py-2 text-right font-bold">{formatCurrency(rows.reduce((s,r) => s+r.cost,0))}</td>
          </tr>
        </tfoot>
      </table>
    </div>
  )
}

// ── Main page ──────────────────────────────────────────────────────



// ─── GST formatting helpers ────────────────────────────────────────
// fmtX: plain $ amount (no AUD prefix — assumed AUD for all documents)
// gstRow: returns three table rows for ex-GST / GST / total presentation
function fmtX(n: number): string {
  return '$' + n.toFixed(2).replace(/\B(?=(\d{3})+(?!\d))/g, ',')
}
function gstRows(exGST: number, label = 'Amount'): string {
  const gst   = exGST * 0.1
  const total = exGST + gst
  return `
    <tr style="border-top:1px solid #e5e7eb">
      <td colspan="999" style="padding:2px"></td>
    </tr>
    <tr>
      <td colspan="2" style="padding:5px 10px;text-align:right;font-size:9.5pt;color:#555">${label} (ex GST)</td>
      <td style="padding:5px 10px;text-align:right;font-family:monospace">${fmtX(exGST)}</td>
    </tr>
    <tr>
      <td colspan="2" style="padding:5px 10px;text-align:right;font-size:9.5pt;color:#555">GST (10%)</td>
      <td style="padding:5px 10px;text-align:right;font-family:monospace">${fmtX(gst)}</td>
    </tr>
    <tr style="background:#f0f9ff">
      <td colspan="2" style="padding:6px 10px;text-align:right;font-weight:bold">Total (inc GST)</td>
      <td style="padding:6px 10px;text-align:right;font-weight:bold;font-family:monospace">${fmtX(total)}</td>
    </tr>`
}

// ─────────────────────────────────────────────────────────────────
// PHASE 6 — LABOUR COST BY JOB REPORT
// Per-job labour cost breakdown — ordinary, overtime, total —
// for job costing and margin tracking.
// ─────────────────────────────────────────────────────────────────
function generateLabourCostByJobReport(timesheets: any[], employees: any[], payRates: Record<string,number>, jobs: any[]) {
  const now    = new Date().toLocaleDateString('en-AU', { day: 'numeric', month: 'long', year: 'numeric' })
  const fmtAUD = fmtX

  const byJob: Record<string, { jobNumber: string; ordHrs: number; otHrs: number; ordCost: number; otCost: number; employees: Set<string> }> = {}

  for (const ts of timesheets) {
    if (ts.status !== 'APPROVED' && ts.status !== 'SUBMITTED') continue
    const emp  = employees.find((e: any) => e.id === ts.employeeId)
    const rate = getEffectiveRate(emp?.payClassification ?? '', (emp as any)?.isAdultApprentice ?? false)
    for (const entry of ts.entries) {
      if (!entry.jobId || !entry.start || !entry.end) continue
      const [sh,sm] = entry.start.split(':').map(Number)
      const [eh,em] = entry.end.split(':').map(Number)
      const hrs = Math.max(0, ((eh*60+em)-(sh*60+sm)-(entry.break ?? 0))/60)
      const ord = Math.min(hrs, 8), ot = Math.max(0, hrs-8)
      if (!byJob[entry.jobId]) byJob[entry.jobId] = { jobNumber: entry.jobNumber, ordHrs: 0, otHrs: 0, ordCost: 0, otCost: 0, employees: new Set() }
      byJob[entry.jobId].ordHrs += ord
      byJob[entry.jobId].otHrs  += ot
      byJob[entry.jobId].ordCost += ord * rate
      byJob[entry.jobId].otCost  += ot * rate * 1.5
      byJob[entry.jobId].employees.add(ts.employeeName)
    }
  }

  const rows = Object.entries(byJob).map(([jobId, d], i) => `
    <tr style="background:${i%2===0?'#fff':'#f9fafb'}">
      <td style="padding:6px 10px;font-weight:bold">${d.jobNumber}</td>
      <td style="padding:6px 8px;text-align:center">${d.employees.size}</td>
      <td style="padding:6px 8px;text-align:right">${d.ordHrs.toFixed(1)}</td>
      <td style="padding:6px 8px;text-align:right">${fmtAUD(d.ordCost)}</td>
      <td style="padding:6px 8px;text-align:right;color:${d.otHrs>0?'#d97706':'#6b7280'}">${d.otHrs.toFixed(1)}</td>
      <td style="padding:6px 8px;text-align:right;color:${d.otHrs>0?'#d97706':'#6b7280'}">${fmtAUD(d.otCost)}</td>
      <td style="padding:6px 8px;text-align:right;font-weight:bold">${fmtAUD(d.ordCost + d.otCost)}</td>
    </tr>`).join('')

  const totalOrdCost = Object.values(byJob).reduce((s,d) => s+d.ordCost, 0)
  const totalOtCost  = Object.values(byJob).reduce((s,d) => s+d.otCost, 0)
  const grandTotal   = totalOrdCost + totalOtCost

  const html = `<!DOCTYPE html><html><head><meta charset="UTF-8">
<title>Labour Cost by Job — ${now}</title>
<style>
*{box-sizing:border-box}body{font-family:Arial,sans-serif;font-size:10pt;color:#111;margin:14mm}
h1{font-size:20pt;font-weight:900;color:#1B3A6B;margin:0}
.hdr{display:flex;justify-content:space-between;align-items:flex-end;border-bottom:3px solid #1B3A6B;padding-bottom:10px;margin-bottom:12px}
.kpi{display:flex;gap:10px;margin-bottom:12px}
.kpi-box{flex:1;border:1px solid #e5e7eb;border-radius:5px;padding:8px;text-align:center}
.kpi-v{font-size:15pt;font-weight:900}
table{width:100%;border-collapse:collapse}
th{background:#1B3A6B;color:#fff;padding:6px 8px;text-align:left;font-size:9.5pt}
.total-row{background:#1B3A6B;color:#fff;font-weight:bold}
.footer{font-size:7.5pt;color:#aaa;text-align:center;margin-top:16px;border-top:1px solid #eee;padding-top:6px}
@media print{body{margin:10mm}}
</style></head><body>
<div class="hdr">
  <div><h1>MCB Civil Electrical</h1><div style="font-size:9pt;color:#888">ABN 12 345 678 901</div></div>
  <div style="text-align:right"><div style="font-size:16pt;font-weight:900;color:#1B3A6B">LABOUR COST BY JOB</div><div style="font-size:9pt;color:#888">As at: ${now}</div></div>
</div>
<div class="kpi">
  <div class="kpi-box"><div class="kpi-v" style="color:#1d4ed8">${fmtAUD(totalOrdCost)}</div><div style="font-size:9pt;color:#6b7280">Ordinary time cost</div></div>
  <div class="kpi-box"><div class="kpi-v" style="color:#d97706">${fmtAUD(totalOtCost)}</div><div style="font-size:9pt;color:#6b7280">Overtime cost</div></div>
  <div class="kpi-box"><div class="kpi-v" style="color:#1B3A6B">${fmtAUD(totalOrdCost+totalOtCost)}</div><div style="font-size:9pt;color:#6b7280">Total labour cost</div></div>
</div>
<table>
  <thead><tr><th>Job #</th><th style="text-align:center">Employees</th><th style="text-align:right">Ord. hrs</th><th style="text-align:right">Ord. cost</th><th style="text-align:right">OT hrs</th><th style="text-align:right">OT cost</th><th style="text-align:right">Total cost</th></tr></thead>
  <tbody>${rows}
    <tr class="total-row"><td style="padding:7px 10px">TOTAL</td><td></td><td></td><td style="padding:7px 8px;text-align:right">${fmtAUD(totalOrdCost)}</td><td></td><td style="padding:7px 8px;text-align:right">${fmtAUD(totalOtCost)}</td><td style="padding:7px 8px;text-align:right">${fmtAUD(totalOrdCost+totalOtCost)}</td></tr>
  </tbody>

  <tr>
    <td colspan="3" style="padding:5px 10px;text-align:right;color:#555">GST (10%)</td>
    <td style="padding:5px 8px;text-align:right;font-family:monospace">${grandTotal * 0.1}</td>
  </tr>
  <tr style="background:#dbeafe">
    <td colspan="3" style="padding:7px 10px;font-weight:bold;text-align:right">Total inc GST</td>
    <td style="padding:7px 8px;font-weight:bold;text-align:right;font-family:monospace">${grandTotal * 1.1}</td>
  </tr>
</table>
<p style="font-size:8.5pt;color:#888;margin-top:8px">Based on approved/submitted timesheets at award pay rates. Overtime calculated at 1.5× for hours above 8/day.</p>
<div class="footer">MCB Civil Electrical · Labour Cost by Job · ${now} · Confidential — internal financial records</div>
</body></html>`
  const w = window.open('', '_blank', 'width=1020,height=750')
  if (!w) { alert('Pop-up blocked'); return }
  w.document.write(html); w.document.close()
  setTimeout(() => { w.focus(); w.print() }, 350)
}


// ─────────────────────────────────────────────────────────────────
// PHASE 6 — OVERTIME ANALYSIS REPORT
// Per-employee overtime hours/cost — flags consistently high OT
// for fatigue management and labour cost review.
// ─────────────────────────────────────────────────────────────────
function generateOvertimeAnalysisReport(timesheets: any[], employees: any[], payRates: Record<string,number>) {
  const now    = new Date().toLocaleDateString('en-AU', { day: 'numeric', month: 'long', year: 'numeric' })
  const fmtAUD = fmtX

  const byEmp: Record<string, { name: string; classification: string; ordHrs: number; otHrs: number; otCost: number; daysWithOt: number }> = {}

  for (const ts of timesheets) {
    if (ts.status !== 'APPROVED' && ts.status !== 'SUBMITTED') continue
    const emp  = employees.find((e: any) => e.id === ts.employeeId)
    const rate = getEffectiveRate(emp?.payClassification ?? '', (emp as any)?.isAdultApprentice ?? false)
    if (!byEmp[ts.employeeId]) byEmp[ts.employeeId] = { name: ts.employeeName, classification: (emp?.payClassification ?? '').replace(/_/g,' '), ordHrs: 0, otHrs: 0, otCost: 0, daysWithOt: 0 }
    for (const entry of ts.entries) {
      if (!entry.start || !entry.end) continue
      const [sh,sm] = entry.start.split(':').map(Number)
      const [eh,em] = entry.end.split(':').map(Number)
      const hrs = Math.max(0, ((eh*60+em)-(sh*60+sm)-(entry.break ?? 0))/60)
      const ord = Math.min(hrs, 8), ot = Math.max(0, hrs-8)
      byEmp[ts.employeeId].ordHrs += ord
      byEmp[ts.employeeId].otHrs  += ot
      byEmp[ts.employeeId].otCost += ot * rate * 1.5
      if (ot > 0) byEmp[ts.employeeId].daysWithOt++
    }
  }

  const sorted = Object.values(byEmp).sort((a,b) => b.otHrs - a.otHrs)
  const rows = sorted.map((d, i) => {
    const otPct = d.ordHrs+d.otHrs > 0 ? (d.otHrs/(d.ordHrs+d.otHrs)*100) : 0
    const flagColor = otPct > 20 ? '#b91c1c' : otPct > 10 ? '#d97706' : '#166534'
    return `<tr style="background:${i%2===0?'#fff':'#f9fafb'}">
      <td style="padding:6px 10px;font-weight:bold">${d.name}</td>
      <td style="padding:6px 8px;font-size:9.5pt">${d.classification}</td>
      <td style="padding:6px 8px;text-align:right">${d.ordHrs.toFixed(1)}</td>
      <td style="padding:6px 8px;text-align:right;font-weight:bold;color:${flagColor}">${d.otHrs.toFixed(1)}</td>
      <td style="padding:6px 8px;text-align:right;color:${flagColor}">${fmtAUD(d.otCost)}</td>
      <td style="padding:6px 8px;text-align:center">${d.daysWithOt}</td>
      <td style="padding:6px 8px;text-align:center;font-weight:bold;color:${flagColor}">${otPct.toFixed(1)}%</td>
      <td style="padding:6px 8px;text-align:center;font-weight:bold;color:${flagColor}">${otPct>20?'⚠ HIGH':otPct>10?'Monitor':'OK'}</td>
    </tr>`
  }).join('')

  const totalOtHrs  = sorted.reduce((s,d) => s+d.otHrs, 0)
  const totalOtCost = sorted.reduce((s,d) => s+d.otCost, 0)
  const highOtCount = sorted.filter(d => (d.otHrs/(d.ordHrs+d.otHrs || 1)*100) > 20).length

  const html = `<!DOCTYPE html><html><head><meta charset="UTF-8">
<title>Overtime Analysis — ${now}</title>
<style>
*{box-sizing:border-box}body{font-family:Arial,sans-serif;font-size:10pt;color:#111;margin:14mm}
h1{font-size:20pt;font-weight:900;color:#1B3A6B;margin:0}
.hdr{display:flex;justify-content:space-between;align-items:flex-end;border-bottom:3px solid #1B3A6B;padding-bottom:10px;margin-bottom:12px}
.kpi{display:flex;gap:10px;margin-bottom:12px}
.kpi-box{flex:1;border:1px solid #e5e7eb;border-radius:5px;padding:8px;text-align:center}
.kpi-v{font-size:15pt;font-weight:900}
table{width:100%;border-collapse:collapse}
th{background:#1B3A6B;color:#fff;padding:6px 8px;text-align:left;font-size:9.5pt}
.footer{font-size:7.5pt;color:#aaa;text-align:center;margin-top:16px;border-top:1px solid #eee;padding-top:6px}
@media print{body{margin:10mm}}
</style></head><body>
<div class="hdr">
  <div><h1>MCB Civil Electrical</h1><div style="font-size:9pt;color:#888">ABN 12 345 678 901</div></div>
  <div style="text-align:right"><div style="font-size:16pt;font-weight:900;color:#1B3A6B">OVERTIME ANALYSIS REPORT</div><div style="font-size:9pt;color:#888">As at: ${now}</div></div>
</div>
<div class="kpi">
  <div class="kpi-box"><div class="kpi-v" style="color:#d97706">${totalOtHrs.toFixed(1)}</div><div style="font-size:9pt;color:#6b7280">Total OT hours</div></div>
  <div class="kpi-box"><div class="kpi-v" style="color:#d97706">${fmtAUD(totalOtCost)}</div><div style="font-size:9pt;color:#6b7280">Total OT cost</div></div>
  <div class="kpi-box"><div class="kpi-v" style="color:${highOtCount>0?'#b91c1c':'#166534'}">${highOtCount}</div><div style="font-size:9pt;color:#6b7280">Employees &gt;20% OT</div></div>
</div>
<table>
  <thead><tr><th>Employee</th><th>Classification</th><th style="text-align:right">Ord. hrs</th><th style="text-align:right">OT hrs</th><th style="text-align:right">OT cost</th><th style="text-align:center">Days w/ OT</th><th style="text-align:center">OT %</th><th style="text-align:center">Flag</th></tr></thead>
  <tbody>${rows}</tbody>
</table>
<p style="font-size:8.5pt;color:#888;margin-top:8px">OT % above 20% may indicate fatigue risk under WHS fatigue management guidelines — review rostering for flagged employees. Based on approved/submitted timesheets this period.</p>
<div class="footer">MCB Civil Electrical · Overtime Analysis Report · ${now} · Confidential</div>
</body></html>`
  const w = window.open('', '_blank', 'width=1100,height=750')
  if (!w) { alert('Pop-up blocked'); return }
  w.document.write(html); w.document.close()
  setTimeout(() => { w.focus(); w.print() }, 350)
}


function exportPayrollCSV(timesheets: any[], employees: any[], payRates: Record<string,number>) {
  const rates    = getPayRates()
  const fmtDate  = (iso: string) => new Date(iso).toLocaleDateString('en-AU')
  const fmtAUD   = (n: number)   => n.toFixed(2)
  const nightPct = getNightShiftLoading() / 100

  // Phase 2 column headers — includes levies and allowances (P6 + L5)
  const rows: string[][] = [[
    'Employee Name','Employee Number','Classification','Employment Type',
    'Pay Period Start','Pay Period End','Pay Period Type',
    // Hours
    'Ordinary Hours','Night Loading Hours','Overtime Hours','Double Time Hours',
    'TAFE Hours','Public Holiday Hours','Total Hours',
    // Pay rates
    'Ordinary Rate ($/hr)',
    // Earnings
    'Ordinary Pay ($)','Night Loading ($)','Overtime Pay ($)','Double Time Pay ($)',
    'Gross Pay ($)',
    // Allowances (employer paid, included in gross)
    'Tool Allowance ($)','Leading Hand Allowance ($)','First Aid Officer Allowance ($)',
    'Height Work Allowance ($)','Confined Space Allowance ($)',
    // Statutory obligations (employer funded, separate from wages)
    'PAYG Withholding Est. ($)',
    'Superannuation (11.5%) ($)',
    'CIPQ Levy (' + fmtAUD(rates.cipqLevyPct) + '%) ($)',
    'BEWT Levy (' + fmtAUD(rates.bewtLevyPct) + '%) ($)',
    // Net
    'Net Pay Est. ($)',
    // References
    'Jobs Worked','Cost Codes','Timesheet ID','Payslip Ref',
  ]]

  for (const ts of timesheets) {
    if (ts.status !== 'APPROVED') continue
    const emp        = employees.find((e: any) => e.id === ts.employeeId) as any
    const rate       = getEffectiveRate(emp?.payClassification ?? '', (emp as any)?.isAdultApprentice ?? false)
    const isTrade    = ['ELECTRICAL','CIVIL'].includes(emp?.trade ?? '')
    const isNight    = ts.entries.some((e: any) => e.shiftType === 'NIGHT')

    let ordH = 0, nightLoadH = 0, otH = 0, dtH = 0, tafeH = 0, phH = 0
    const jobsSet  = new Set<string>()
    const codesSet = new Set<string>()

    for (const entry of ts.entries) {
      if (entry.entryType === 'TAFE_ATTENDANCE') { tafeH += 6; continue }
      if (entry.entryType === 'PUBLIC_HOLIDAY')  { phH   += entry.hoursWorked ?? 8; continue }
      if (!entry.start || !entry.end) continue
      const h = calcHours(entry.start, entry.end, entry.break ?? 0, entry.dayIndex, entry.shiftType ?? 'DAY')
      ordH       += h.ordinary
      nightLoadH += h.nightLoading
      otH        += h.overtime
      dtH        += h.doubleTime
      if (entry.jobNumber) jobsSet.add(entry.jobNumber)
      if (entry.costCode)  codesSet.add(entry.costCode)
    }

    const totalH      = ordH + otH + dtH + tafeH
    const ordPay      = ordH  * rate
    const nightLoadPay= nightLoadH * rate
    const otPay       = otH   * rate * (rates.otMultiplier ?? 1.5)
    const dtPay       = dtH   * rate * (rates.dtMultiplier ?? 2.0)
    const tafePay     = tafeH * rate
    const phPay       = phH   * rate * 2.5   // public holiday rate

    const grossPay    = ordPay + nightLoadPay + otPay + dtPay + tafePay + phPay

    // Allowances
    const toolAllow   = isTrade ? rates.allowances.toolAllowance : 0
    const lhAllow     = 0   // leading hand — requires designation flag (Phase 3)
    const faoAllow    = 0   // first aid officer — requires designation flag (Phase 3)
    const heightAllow = 0   // per-hour — requires task flag (Phase 3)
    const csAllow     = 0   // confined space — requires task flag (Phase 3)

    // Employer obligations (not deducted from wages — employer pays separately)
    const payg    = +(grossPay * 0.185).toFixed(2)
    const super_  = +(grossPay * 0.115).toFixed(2)
    const cipqLev = +(ordPay   * rates.cipqLevyPct / 100).toFixed(2)
    const bewtLev = +(ordPay   * rates.bewtLevyPct / 100).toFixed(2)
    const net     = +(grossPay - payg).toFixed(2)

    const period       = getPayPeriod()
    const periodEndMs  = new Date(ts.weekStarting).getTime() + (period === 'FORTNIGHTLY' ? 13 : 6) * 86400000
    const payslipRef   = `PS-${emp?.employeeNumber ?? ts.employeeId}-${ts.weekStarting}`

    rows.push([
      ts.employeeName,
      emp?.employeeNumber ?? '',
      (emp?.payClassification ?? '').replace(/_/g,' '),
      emp?.employmentType ?? '',
      fmtDate(ts.weekStarting),
      fmtDate(new Date(periodEndMs).toISOString().slice(0,10)),
      period,
      ordH.toFixed(2),
      nightLoadH.toFixed(4),
      otH.toFixed(2),
      dtH.toFixed(2),
      tafeH.toFixed(2),
      phH.toFixed(2),
      totalH.toFixed(2),
      fmtAUD(rate),
      fmtAUD(ordPay),
      fmtAUD(nightLoadPay),
      fmtAUD(otPay),
      fmtAUD(dtPay),
      fmtAUD(grossPay),
      fmtAUD(toolAllow),
      fmtAUD(lhAllow),
      fmtAUD(faoAllow),
      fmtAUD(heightAllow),
      fmtAUD(csAllow),
      fmtAUD(payg),
      fmtAUD(super_),
      fmtAUD(cipqLev),
      fmtAUD(bewtLev),
      fmtAUD(net),
      Array.from(jobsSet).join('; '),
      Array.from(codesSet).join('; '),
      ts.id,
      payslipRef,
    ])
  }

  if (rows.length === 1) { alert('No approved timesheets to export'); return }

  // R3: Lock all APPROVED timesheets after export
  for (const ts of timesheets) {
    if (ts.status === 'APPROVED') {
      ts.status     = 'PROCESSED'
      ts.lockedAt   = new Date().toISOString()
      ts.lockedByName = 'Payroll export'
    }
  }

  const csv  = rows.map(r => r.map(c => `"${String(c).replace(/"/g,'""')}`).join(',')).join('\n')
  const blob = new Blob(['\uFEFF' + csv], { type: 'text/csv;charset=utf-8' })
  const url  = URL.createObjectURL(blob)
  const a    = document.createElement('a')
  a.href     = url
  a.download = `MCB_Payroll_Export_P2_${new Date().toISOString().slice(0,10)}.csv`
  document.body.appendChild(a); a.click()
  document.body.removeChild(a); URL.revokeObjectURL(url)
}

// ─────────────────────────────────────────────────────────────────
// PHASE 2 — ABA BANK FILE EXPORT
// Australian Banking Association (de Lacey) format for direct bank
// payroll disbursement. One credit record per approved timesheet.
// Upload to your business banking portal under Payroll / batch payments.
// BSB/account numbers are placeholder until live employee records connected.
// ─────────────────────────────────────────────────────────────────
function exportABAFile(timesheets: any[], employees: any[]) {
  const rates = getPayRates()

  // Originating company bank account — replace with real details before live use
  const CO_BSB     = '014-001'
  const CO_ACCOUNT = '123456789'
  const CO_NAME    = 'MCB CIVIL ELECTRICAL'
  const BANK_MNEM  = 'ANZ'
  const APCA_ID    = '301500'
  const DESCRIPTION = 'PAYROLL'

  const approved = timesheets.filter((t: any) => t.status === 'APPROVED')
  if (approved.length === 0) {
    alert('No approved timesheets to export. Approve timesheets before generating the ABA file.')
    return
  }

  const payments: Array<{ bsb: string; account: string; name: string; netCents: number; ref: string }> = []

  for (const ts of approved) {
    const emp     = employees.find((e: any) => e.id === ts.employeeId) as any
    const rate    = getEffectiveRate(emp?.payClassification ?? '', (emp as any)?.isAdultApprentice ?? false)
    const isTrade = ['ELECTRICAL', 'CIVIL'].includes(emp?.trade ?? '')

    let ordH = 0, nightLoadH = 0, otH = 0, dtH = 0, tafeH = 0, phH = 0
    for (const entry of ts.entries) {
      if (entry.entryType === 'TAFE_ATTENDANCE') { tafeH += 6; continue }
      if (entry.entryType === 'PUBLIC_HOLIDAY')  { phH   += entry.hoursWorked ?? 8; continue }
      if (!entry.start || !entry.end) continue
      const h = calcHours(entry.start, entry.end, entry.break ?? 0, entry.dayIndex, entry.shiftType ?? 'DAY')
      ordH       += h.ordinary
      nightLoadH += h.nightLoading
      otH        += h.overtime
      dtH        += h.doubleTime
    }

    const toolAllow = isTrade ? rates.allowances.toolAllowance : 0
    const grossPay  = ordH * rate + nightLoadH * rate
                    + otH * rate * (rates.otMultiplier ?? 1.5)
                    + dtH * rate * (rates.dtMultiplier ?? 2.0)
                    + tafeH * rate + phH * rate * 2.5 + toolAllow
    const net       = +(grossPay - grossPay * 0.185).toFixed(2)
    const netCents  = Math.round(net * 100)

    const bsb     = ((emp?.bankBSB     ?? '014-001')   + '').replace(/[^0-9-]/g, '').slice(0, 7).padEnd(7, '0')
    const account = ((emp?.bankAccount ?? '000000001') + '').replace(/[^0-9]/g, '').slice(0, 9)
    const empName = ts.employeeName.toUpperCase().replace(/[^A-Z0-9 ]/g, '').slice(0, 32)
    const ref     = ('SALARY ' + ts.weekStarting.replace(/-/g, '')).slice(0, 18)

    if (netCents > 0) payments.push({ bsb, account, name: empName, netCents, ref })
  }

  if (payments.length === 0) {
    alert('No payable amounts calculated. Check approved timesheets have hours entered.')
    return
  }

  const pad  = (s: string, len: number) => s.padEnd(len).slice(0, len)
  const zpad = (n: number, len: number) => String(n).padStart(len, '0').slice(-len)

  const now     = new Date()
  const abaDate = String(now.getDate()).padStart(2, '0')
                + String(now.getMonth() + 1).padStart(2, '0')
                + String(now.getFullYear()).slice(-2)

  const lines: string[] = []

  // Type 0 — Descriptive (header) record
  lines.push(
    '0' + ' '.repeat(17) + '1' +
    pad(BANK_MNEM, 3) + ' '.repeat(7) +
    pad(CO_NAME, 26) + zpad(Number(APCA_ID), 6) +
    pad(DESCRIPTION, 12) + abaDate + ' '.repeat(40)
  )

  let creditTotal = 0
  const debitTotal  = 0

  // Type 1 — Detail record per employee
  for (const p of payments) {
    creditTotal += p.netCents
    lines.push(
      '1' +
      ' ' + pad(p.bsb.replace('-', ''), 6) +
      pad(p.account, 9) +
      ' ' + '50' +
      zpad(p.netCents, 10) +
      pad(p.name, 32) +
      pad(p.ref, 18) +
      ' ' + pad(CO_BSB.replace('-', ''), 6) +
      pad(CO_ACCOUNT, 9) +
      pad(CO_NAME.slice(0, 16), 16) +
      zpad(0, 8)
    )
  }

  // Type 7 — File total record
  lines.push(
    '7' + '999-999' + ' '.repeat(12) +
    zpad(Math.abs(creditTotal - debitTotal), 10) +
    zpad(creditTotal, 10) +
    zpad(debitTotal, 10) +
    ' '.repeat(24) +
    zpad(payments.length, 6) +
    ' '.repeat(40)
  )

  const blob = new Blob([lines.join('\r\n')], { type: 'text/plain;charset=utf-8' })
  const url  = URL.createObjectURL(blob)
  const a    = document.createElement('a')
  a.href     = url
  a.download = 'MCB_Payroll_' + now.toISOString().slice(0, 10) + '.aba'
  document.body.appendChild(a); a.click()
  document.body.removeChild(a); URL.revokeObjectURL(url)
}

// ─────────────────────────────────────────────────────────────────
// PHASE 2 — PLSL CONTRIBUTION REPORT (L5 / P6)
// Quarterly CIPQ and BEWT levy report for lodgement.
// Employer obligations calculated from approved timesheets.
// ─────────────────────────────────────────────────────────────────
function generatePLSLReport(timesheets: any[], employees: any[], payRates: Record<string,number>) {
  const rates   = getPayRates()
  const b       = getDocumentBranding()
  const now     = new Date()
  const header  = buildDocumentHeader('INVOICE', 'CIPQ-REPORT')  // INVOICE docType for financial reports
  const footer  = buildDocumentFooter('INVOICE', 'CIPQ-REPORT')
  const accent  = b.accentColor

  // Calculate quarterly totals per employee
  const approved = timesheets.filter(ts => ts.status === 'APPROVED')
  const rows = approved.map(ts => {
    const emp    = employees.find((e: any) => e.id === ts.employeeId) as any
    const rate   = getEffectiveRate(emp?.payClassification ?? '', (emp as any)?.isAdultApprentice ?? false)
    let ordWages = 0

    for (const entry of ts.entries) {
      if (entry.entryType === 'TAFE_ATTENDANCE' || !entry.start || !entry.end) continue
      const h = calcHours(entry.start, entry.end, entry.break ?? 0, entry.dayIndex, entry.shiftType ?? 'DAY')
      ordWages += h.ordinary * rate
    }
    const cipq  = +(ordWages * rates.cipqLevyPct / 100).toFixed(2)
    const bewt  = +(ordWages * rates.bewtLevyPct / 100).toFixed(2)
    const total = +(cipq + bewt).toFixed(2)
    return { name: ts.employeeName, empNo: emp?.employeeNumber ?? '', classification: (emp?.payClassification ?? '').replace(/_/g,' '), empType: emp?.employmentType ?? '', weekStart: ts.weekStarting, ordWages, cipq, bewt, total }
  })

  const grandOrdWages = rows.reduce((s, r) => s + r.ordWages, 0)
  const grandCIPQ     = rows.reduce((s, r) => s + r.cipq, 0)
  const grandBEWT     = rows.reduce((s, r) => s + r.bewt, 0)
  const grandTotal    = grandCIPQ + grandBEWT

  // Next quarter end + 28 days payment due
  const qEnd     = new Date(now.getFullYear(), Math.floor(now.getMonth() / 3) * 3 + 3, 0)
  const dueDate  = new Date(qEnd.getTime() + 28 * 86400000)
  const fmtD     = (d: Date) => d.toLocaleDateString('en-AU', { day:'numeric', month:'long', year:'numeric' })

  const html = `<!DOCTYPE html><html lang="en"><head><meta charset="UTF-8">
<title>CIPQ &amp; BEWT Levy Report</title>
<style>
  @page { size: A4; margin: 18mm; }
  * { box-sizing:border-box; margin:0; padding:0; }
  body { font-family: ${b.fontFamily},Arial,sans-serif; font-size:9.5pt; color:#1f2937; }
  h1 { font-size:16pt; font-weight:900; color:${accent}; margin-bottom:4px; }
  .subtitle { font-size:9pt; color:#6B7280; margin-bottom:16px; }
  .info-grid { display:grid; grid-template-columns:1fr 1fr 1fr; gap:12px; margin-bottom:16px; }
  .info-box { background:#F8FAFC; border:1px solid #E5E7EB; border-radius:6px; padding:10px 14px; }
  .info-box .lbl { font-size:7.5pt; color:#9CA3AF; margin-bottom:2px; }
  .info-box .val { font-weight:700; font-size:10.5pt; }
  table { width:100%; border-collapse:collapse; margin-bottom:16px; }
  th { background:${accent}; color:#fff; font-size:8pt; font-weight:600; padding:7px 10px; text-align:left; }
  td { padding:6px 10px; border-bottom:1px solid #F3F4F6; font-size:9pt; }
  tr:nth-child(even) td { background:#FAFAFA; }
  .tar { text-align:right; }
  .tfoot td { background:#1f2937; color:#fff; font-weight:700; border-top:2px solid ${accent}; }
  .notice { background:#FEF3C7; border-left:4px solid #F59E0B; padding:12px 16px; margin-bottom:16px; border-radius:0 6px 6px 0; font-size:9pt; }
  .notice strong { color:#92400E; }
  .legal { font-size:7.5pt; color:#9CA3AF; background:#F8FAFC; padding:10px 14px; border-radius:6px; margin-top:12px; }
</style>
</head><body>
${header}

<h1>CIPQ &amp; BEWT Levy Contribution Report</h1>
<p class="subtitle">Generated ${fmtD(now)} &nbsp;·&nbsp; ${b.companyName} &nbsp;·&nbsp; ABN ${b.abn}</p>

<div class="info-grid">
  <div class="info-box"><div class="lbl">Reporting period</div><div class="val">Current approved timesheets</div></div>
  <div class="info-box"><div class="lbl">CIPQ levy rate</div><div class="val">${rates.cipqLevyPct}% of ordinary wages</div></div>
  <div class="info-box"><div class="lbl">BEWT levy rate</div><div class="val">${rates.bewtLevyPct}% of ordinary wages</div></div>
</div>

<div class="notice">
  <strong>Payment due: ${fmtD(dueDate)}</strong> (28 days after quarter ending ${fmtD(qEnd)})<br/>
  CIPQ contributions must be lodged via <strong>cipq.com.au</strong>. BEWT contributions lodged via the relevant body.
  Both are employer-funded obligations — do not deduct from employee wages.
</div>

<table>
  <thead>
    <tr>
      <th>Employee</th><th>Emp #</th><th>Classification</th><th>Type</th>
      <th class="tar">Period</th>
      <th class="tar">Ordinary wages ($)</th>
      <th class="tar">CIPQ (${rates.cipqLevyPct}%) ($)</th>
      <th class="tar">BEWT (${rates.bewtLevyPct}%) ($)</th>
      <th class="tar">Total levy ($)</th>
    </tr>
  </thead>
  <tbody>
    ${rows.map(r => `<tr>
      <td>${r.name}</td>
      <td>${r.empNo}</td>
      <td>${r.classification}</td>
      <td>${r.empType}</td>
      <td class="tar">${r.weekStart}</td>
      <td class="tar">${r.ordWages.toFixed(2)}</td>
      <td class="tar">${r.cipq.toFixed(2)}</td>
      <td class="tar">${r.bewt.toFixed(2)}</td>
      <td class="tar"><strong>${r.total.toFixed(2)}</strong></td>
    </tr>`).join('')}
  </tbody>
  <tfoot>
    <tr class="tfoot">
      <td colspan="5"><strong>TOTALS</strong></td>
      <td class="tar"><strong>$${grandOrdWages.toFixed(2)}</strong></td>
      <td class="tar"><strong>$${grandCIPQ.toFixed(2)}</strong></td>
      <td class="tar"><strong>$${grandBEWT.toFixed(2)}</strong></td>
      <td class="tar"><strong>$${grandTotal.toFixed(2)}</strong></td>
    </tr>
  </tfoot>
</table>

<div class="legal">
  <strong>Notes:</strong> Ordinary wages exclude overtime loadings, penalty rates, and allowances. Levies are calculated on base ordinary time wages as per CIPQ and BEWT levy provisions.
  PLSL accrual rate: 1 week per year of service (8.67 weeks per 10 years). This report is a guide only — confirm calculations with your payroll advisor before lodging.
  ${b.companyName} · ABN ${b.abn}
</div>

${footer}
</body></html>`

  const blob = new Blob([html], { type: 'text/html' })
  const url  = URL.createObjectURL(blob)
  const a    = document.createElement('a')
  a.href     = url
  a.download = `CIPQ_BEWT_Report_${now.toISOString().slice(0,10)}.html`
  document.body.appendChild(a); a.click()
  document.body.removeChild(a); URL.revokeObjectURL(url)
}


// ─────────────────────────────────────────────────────────────────
// PHASE 2 — PAYSLIP GENERATION (P7 full)
// Replaces Phase 1 hardcoded header with buildDocumentHeader() from
// documentBranding.ts — pulls company name, ABN, address, logo from
// the branding config set in Settings > Branding & Signatures.
// Phase 2 additions: allowances section, leave balance snapshot,
// CIPQ/BEWT employer disclosure, YTD section.
// ─────────────────────────────────────────────────────────────────
function generatePayslip(ts: TS, payRate: number) {
  const h          = totalHours(ts)
  const gross      = estimatedGross(ts, payRate)
  const rates      = getPayRates()
  const b          = getDocumentBranding()
  const leaveBal   = getLeaveBalance(ts.employeeId)
  const emp        = MOCK_EMPLOYEES.find((e: any) => e.id === ts.employeeId) as any
  const isTrade    = ['ELECTRICAL','CIVIL'].includes(emp?.trade ?? '')
  const nightLoad$ = h.nightLoading * payRate
  const otPay$     = h.overtime  * payRate * (rates.otMultiplier ?? 1.5)
  const dtPay$     = h.doubleTime * payRate * (rates.dtMultiplier ?? 2.0)
  const toolAllow  = isTrade ? rates.allowances.toolAllowance : 0
  const grossTotal = gross + toolAllow   // tool allowance included in gross pay
  const payg$      = +(grossTotal * 0.185).toFixed(2)
  const super$     = +(grossTotal * 0.115).toFixed(2)
  const cipq$      = +(h.ordinary * payRate * rates.cipqLevyPct / 100).toFixed(2)
  const bewt$      = +(h.ordinary * payRate * rates.bewtLevyPct / 100).toFixed(2)
  const net$       = +(grossTotal - payg$).toFixed(2)
  const period     = getPayPeriod()
  const payslipRef = `PS-${emp?.employeeNumber ?? ts.employeeId}-${ts.weekStarting}`
  const now        = new Date().toLocaleDateString('en-AU', { day:'numeric', month:'long', year:'numeric' })
  const accent     = b.accentColor

  // Phase 2: branded header from documentBranding.ts
  const brandedHeader = buildDocumentHeader('TIMESHEET', payslipRef)
  const brandedFooter = buildDocumentFooter('TIMESHEET', payslipRef)

  const html = `<!DOCTYPE html>
<html lang="en">
<head>
<meta charset="UTF-8">
<title>Payslip — ${ts.employeeName} — ${ts.weekStarting}</title>
<style>
  @page { size:A4; margin:18mm; }
  * { box-sizing:border-box; margin:0; padding:0; }
  body { font-family:${b.fontFamily},Arial,sans-serif; font-size:9.5pt; color:#1f2937; background:#fff; }
  .doc-badge { display:flex; justify-content:space-between; align-items:center; margin-bottom:14px; }
  .doc-badge .title { font-size:16pt; font-weight:900; color:${accent}; }
  .doc-badge .ref   { font-size:9pt; color:#6B7280; }
  .section { border:1px solid #E5E7EB; border-radius:6px; margin-bottom:10px; overflow:hidden; }
  .section-hdr { background:#F8FAFC; font-weight:700; font-size:8pt; text-transform:uppercase; letter-spacing:.05em; color:#6B7280; padding:6px 14px; border-bottom:1px solid #E5E7EB; }
  .grid2 { display:grid; grid-template-columns:1fr 1fr; }
  .field { padding:7px 14px; border-bottom:1px solid #F3F4F6; }
  .field:last-child { border-bottom:none; }
  .field .lbl { font-size:7.5pt; color:#9CA3AF; margin-bottom:1px; }
  .field .val { font-weight:600; }
  table { width:100%; border-collapse:collapse; }
  th { background:#F8FAFC; font-size:8pt; font-weight:600; text-align:left; padding:6px 12px; border-bottom:1px solid #E5E7EB; }
  td { padding:6px 12px; border-bottom:1px solid #F9FAFB; font-size:9pt; }
  .tar { text-align:right; }
  .total-row td { background:#F0FDF4; font-weight:700; border-top:2px solid #D1FAE5; }
  .deduct td { color:#991B1B; }
  .net-row td { background:${accent}; color:#fff; font-weight:900; font-size:10.5pt; }
  .employer-row td { color:#6B7280; font-size:8.5pt; font-style:italic; }
  .leave-grid { display:grid; grid-template-columns:1fr 1fr 1fr 1fr; gap:0; }
  .leave-item { padding:8px 12px; border-right:1px solid #F3F4F6; }
  .leave-item:last-child { border-right:none; }
  .leave-item .lbl { font-size:7.5pt; color:#9CA3AF; }
  .leave-item .val { font-weight:700; font-size:10pt; color:${accent}; }
  .legal { font-size:7.5pt; color:#9CA3AF; margin-top:8px; }
</style>
</head>
<body>
${brandedHeader}
<div class="doc-badge">
  <div class="title">PAYSLIP</div>
  <div class="ref">Ref: ${payslipRef}</div>
</div>

<div class="section">
  <div class="section-hdr">Employee details</div>
  <div class="grid2">
    <div class="field"><div class="lbl">Employee name</div><div class="val">${ts.employeeName}</div></div>
    <div class="field"><div class="lbl">Pay period type</div><div class="val">${period}</div></div>
    <div class="field"><div class="lbl">Employee number</div><div class="val">${emp?.employeeNumber ?? '—'}</div></div>
    <div class="field"><div class="lbl">Pay period commencing</div><div class="val">${ts.weekStarting}</div></div>
    <div class="field"><div class="lbl">Classification</div><div class="val">${(emp?.payClassification ?? '').replace(/_/g,' ')}</div></div>
    <div class="field"><div class="lbl">Date issued</div><div class="val">${now}</div></div>
    <div class="field"><div class="lbl">Ordinary rate</div><div class="val">$${payRate.toFixed(2)}/hr</div></div>
    <div class="field"><div class="lbl">Night shift loading</div><div class="val">${getNightShiftLoading()}% of ordinary hours</div></div>
  </div>
</div>

<div class="section">
  <div class="section-hdr">Earnings this pay period</div>
  <table>
    <tr><th>Description</th><th>Hours / Qty</th><th class="tar">Rate</th><th class="tar">Amount ($)</th></tr>
    ${h.ordinary   > 0 ? `<tr><td>Ordinary hours</td><td>${h.ordinary.toFixed(2)}h</td><td class="tar">$${payRate.toFixed(2)}</td><td class="tar">$${(h.ordinary*payRate).toFixed(2)}</td></tr>` : ''}
    ${h.nightLoading > 0 ? `<tr><td>Night shift loading (${getNightShiftLoading()}%)</td><td>${h.ordinary.toFixed(2)}h base</td><td class="tar">${getNightShiftLoading()}%</td><td class="tar">$${nightLoad$.toFixed(2)}</td></tr>` : ''}
    ${h.overtime   > 0 ? `<tr><td>Overtime (${rates.otMultiplier ?? 1.5}×)</td><td>${h.overtime.toFixed(2)}h</td><td class="tar">$${(payRate*(rates.otMultiplier??1.5)).toFixed(2)}</td><td class="tar">$${otPay$.toFixed(2)}</td></tr>` : ''}
    ${h.doubleTime > 0 ? `<tr><td>Double time (${rates.dtMultiplier ?? 2.0}×)</td><td>${h.doubleTime.toFixed(2)}h</td><td class="tar">$${(payRate*(rates.dtMultiplier??2.0)).toFixed(2)}</td><td class="tar">$${dtPay$.toFixed(2)}</td></tr>` : ''}
    ${toolAllow  > 0 ? `<tr><td>Tool allowance (trade employees)</td><td>1 week</td><td class="tar">$${toolAllow.toFixed(2)}</td><td class="tar">$${toolAllow.toFixed(2)}</td></tr>` : ''}
    <tr class="total-row"><td colspan="3"><strong>Gross earnings</strong></td><td class="tar"><strong>$${grossTotal.toFixed(2)}</strong></td></tr>
  </table>
</div>

<div class="section">
  <div class="section-hdr">Deductions and statutory contributions</div>
  <table>
    <tr><th>Item</th><th class="tar">Amount ($)</th></tr>
    <tr class="deduct"><td>PAYG withholding (est. 18.5% — confirm with your tax agent or myTax)</td><td class="tar">−$${payg$.toFixed(2)}</td></tr>
    <tr class="net-row"><td><strong>Net pay (after PAYG)</strong></td><td class="tar"><strong>$${net$.toFixed(2)}</strong></td></tr>
    <tr class="employer-row"><td>Superannuation guarantee (11.5%) — employer-funded, not deducted from wages</td><td class="tar">$${super$.toFixed(2)}</td></tr>
    <tr class="employer-row"><td>CIPQ portable long service leave levy (${rates.cipqLevyPct}%) — employer obligation</td><td class="tar">$${cipq$.toFixed(2)}</td></tr>
    <tr class="employer-row"><td>BEWT training levy (${rates.bewtLevyPct}%) — employer obligation</td><td class="tar">$${bewt$.toFixed(2)}</td></tr>
  </table>
  <p style="font-size:7.5pt;color:#9CA3AF;padding:6px 12px;">Employer-funded items (super, CIPQ, BEWT) are obligations of the employer — they are not deducted from your wages.</p>
</div>

${leaveBal ? `
<div class="section">
  <div class="section-hdr">Leave balance snapshot (as at ${now})</div>
  <div class="leave-grid">
    <div class="leave-item"><div class="lbl">Annual leave</div><div class="val">${formatLeaveBalance(leaveBal.annualLeave)}</div></div>
    <div class="leave-item"><div class="lbl">Personal / sick leave</div><div class="val">${formatLeaveBalance(leaveBal.personalLeave)}</div></div>
    <div class="leave-item"><div class="lbl">TOIL balance</div><div class="val">${formatLeaveBalance(leaveBal.toil)}</div></div>
    <div class="leave-item"><div class="lbl">Long service (CIPQ)</div><div class="val">${formatLeaveBalance(leaveBal.longServiceLeave)}</div></div>
  </div>
</div>` : ''}

<p class="legal">
  This payslip is a summary document generated by the TrafficProcure platform (${b.companyName}).
  PAYG withholding shown is an estimate only — your actual withholding may vary based on your tax file number declaration, study debt (HELP/VSL), and Medicare levy.
  Confirm your tax position with a registered tax agent or via myTax at ato.gov.au.
  Superannuation will be paid to your nominated fund within 28 days of the end of each quarter.
</p>

${brandedFooter}
</body>
</html>`

  const blob = new Blob([html], { type: 'text/html' })
  const url  = URL.createObjectURL(blob)
  const a    = document.createElement('a')
  a.href     = url
  a.download = `Payslip_${ts.employeeName.replace(/\s+/g,'_')}_${ts.weekStarting}_${payslipRef}.html`
  a.click()
  URL.revokeObjectURL(url)
}


// ─────────────────────────────────────────────────────────────────
// PHASE 1 — RECONCILIATION VIEW (R1)
// Planned (roster) vs actual (timesheet) vs mobile-confirmed hours
// ─────────────────────────────────────────────────────────────────
function ReconciliationTab() {
  const allAllocs    = getRosterAllocations()
  const allTS        = MOCK_TIMESHEETS
  const VARIANCE_THRESHOLD = 1.0  // hours — flag if difference exceeds this

  // Build reconciliation table: per employee per day
  const empIds = [...new Set([
    ...allAllocs.map(a => a.employeeId),
    ...allTS.flatMap(ts => ts.entries.map(() => ts.employeeId)),
  ])]

  const rows = empIds.map(empId => {
    const emp       = MOCK_EMPLOYEES.find((e: any) => e.id === empId) as any
    const allocs    = allAllocs.filter(a => a.employeeId === empId)
    const tsEntries = allTS.find(ts => ts.employeeId === empId)
    const entries   = tsEntries?.entries ?? []
    const rosterHrs = allocs.length * 8    // simplified: 8h per allocation
    const tsHrs     = entries.reduce((sum, e) => sum + shiftHours(e).total, 0)
    const variance  = +(tsHrs - rosterHrs).toFixed(2)
    const hasFlag   = Math.abs(variance) > VARIANCE_THRESHOLD && rosterHrs > 0
    return { empId, name: emp?.fullName ?? empId, rosterHrs, tsHrs, variance, hasFlag,
             tsStatus: tsEntries?.status ?? null }
  })

  return (
    <div className="space-y-4">
      <div className="bg-blue-50 border border-blue-200 rounded-xl px-4 py-3 text-xs text-blue-800">
        <strong>Planned vs actual reconciliation.</strong> Compares rostered allocations (planned hours) against approved timesheet entries (actual hours). Variances above {VARIANCE_THRESHOLD}h are flagged for PM review before payroll export.
      </div>

      <div className="bg-white border border-gray-200 rounded-xl overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="text-xs text-gray-500 bg-gray-50 border-b">
                <th className="text-left px-4 py-3 font-medium">Employee</th>
                <th className="text-right px-3 py-3 font-medium">Rostered (planned)</th>
                <th className="text-right px-3 py-3 font-medium">Timesheet (actual)</th>
                <th className="text-right px-3 py-3 font-medium">Variance</th>
                <th className="text-center px-3 py-3 font-medium">TS Status</th>
                <th className="text-center px-3 py-3 font-medium">Flag</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-50">
              {rows.map(r => (
                <tr key={r.empId} className={r.hasFlag ? 'bg-amber-50/40' : 'hover:bg-gray-50'}>
                  <td className="px-4 py-3 font-medium text-gray-900">{r.name}</td>
                  <td className="px-3 py-3 text-right tabular-nums text-gray-600">{r.rosterHrs.toFixed(1)}h</td>
                  <td className="px-3 py-3 text-right tabular-nums text-gray-900 font-medium">{r.tsHrs.toFixed(1)}h</td>
                  <td className={`px-3 py-3 text-right tabular-nums font-semibold ${r.variance > 0 ? 'text-amber-600' : r.variance < 0 ? 'text-red-600' : 'text-green-600'}`}>
                    {r.variance > 0 ? '+' : ''}{r.variance.toFixed(1)}h
                  </td>
                  <td className="px-3 py-3 text-center">
                    {r.tsStatus ? (
                      <span className={`text-xs px-2 py-0.5 rounded-full font-medium ${
                        r.tsStatus === 'APPROVED' ? 'bg-green-100 text-green-700' :
                        r.tsStatus === 'SUBMITTED' ? 'bg-blue-100 text-blue-700' :
                        'bg-gray-100 text-gray-500'
                      }`}>{r.tsStatus}</span>
                    ) : <span className="text-gray-300 text-xs">—</span>}
                  </td>
                  <td className="px-3 py-3 text-center">
                    {r.hasFlag ? (
                      <span className="text-xs bg-amber-100 text-amber-700 px-1.5 py-0.5 rounded-full font-medium">Review</span>
                    ) : (
                      <span className="text-xs text-green-600">✓</span>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
      <p className="text-xs text-gray-400">
        Roster hours are estimated from allocation count × 8h standard day. Exact planned hours will be available once the backend roster API is connected in Phase 1 production build.
      </p>
    </div>
  )
}

// ─────────────────────────────────────────────────────────────────
// PHASE 4 — O1: RESOURCE FORECAST VIEW
// 4-week forward headcount showing required vs available per job.
// Available = all employees minus those on leave, FM-blocked, or unavailable.
// Required = from job plans (prototype: estimated from current allocation density).
// ─────────────────────────────────────────────────────────────────
function ResourceForecastTab() {
  const allocations   = getRosterAllocations()
  const weekCap       = getWeeklyHoursCap()
  const today         = new Date()

  // Build 4 future weeks starting from current Monday
  const getMonday = (d: Date) => {
    const day = d.getDay(); const diff = (day === 0 ? -6 : 1 - day)
    return new Date(d.getFullYear(), d.getMonth(), d.getDate() + diff)
  }
  const monday = getMonday(today)

  const weeks = Array.from({ length: 6 }, (_, i) => {
    const weekStart = new Date(monday.getTime() + i * 7 * 86400000)
    const dates = Array.from({ length: 5 }, (_, d) =>
      new Date(weekStart.getTime() + d * 86400000).toISOString().slice(0, 10)
    )
    const label = weekStart.toLocaleDateString('en-AU', { day: 'numeric', month: 'short' })
    return { weekStart: dates[0], label, dates }
  })

  // Per-week per-employee capacity analysis
  const analysis = weeks.map(week => {
    const allocated = new Set<string>()
    const byJob: Record<string, Set<string>> = {}
    let totalShifts = 0

    for (const a of allocations) {
      if (week.dates.includes(a.date)) {
        allocated.add(a.employeeId)
        if (!byJob[a.jobId]) byJob[a.jobId] = new Set()
        byJob[a.jobId].add(a.employeeId)
        totalShifts++
      }
    }

    const utilisation = MOCK_EMPLOYEES.length > 0
      ? Math.round((allocated.size / MOCK_EMPLOYEES.length) * 100) : 0

    return {
      ...week,
      allocatedCount: allocated.size,
      totalEmployees: MOCK_EMPLOYEES.length,
      availableCount: MOCK_EMPLOYEES.length,   // simplified: all available (Phase 3 leave gate refines this)
      utilisation,
      totalShifts,
      byJob: Object.fromEntries(Object.entries(byJob).map(([k, v]) => [k, v.size])),
    }
  })

  const jobs = [...new Set(allocations.map(a => a.jobId))]
    .map(jid => MOCK_JOBS.find(j => j.id === jid))
    .filter(Boolean) as typeof MOCK_JOBS

  return (
    <div className="space-y-5">
      <div className="bg-blue-50 border border-blue-200 rounded-xl px-4 py-3 text-xs text-blue-800">
        <strong>Resource forecast — 6 week forward view.</strong> Shows crew allocation density from current roster data. Weeks with {'<'}60% utilisation may have capacity for additional work scope. Weeks at {'>'}{Math.round(weekCap * 0.8)}h average risk crew fatigue.
      </div>

      {/* Utilisation summary cards */}
      <div className="grid grid-cols-3 sm:grid-cols-6 gap-3">
        {analysis.map(w => {
          const colour = w.utilisation >= 90 ? 'bg-red-100 text-red-700 border-red-200'
            : w.utilisation >= 70 ? 'bg-amber-100 text-amber-700 border-amber-200'
            : 'bg-green-100 text-green-700 border-green-200'
          return (
            <div key={w.weekStart} className={`border rounded-xl p-3 text-center ${colour}`}>
              <div className="text-xl font-bold">{w.utilisation}%</div>
              <div className="text-xs mt-0.5 font-medium">{w.label}</div>
              <div className="text-[10px] mt-0.5 opacity-70">{w.allocatedCount}/{w.totalEmployees} crew</div>
            </div>
          )
        })}
      </div>

      {/* Per-job breakdown */}
      <div className="bg-white border border-gray-200 rounded-xl overflow-hidden">
        <div className="px-5 py-3.5 border-b bg-gray-50">
          <h3 className="font-semibold text-gray-900 text-sm">Crew allocation by job — 6 week view</h3>
        </div>
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="text-xs text-gray-500 bg-gray-50 border-b">
                <th className="text-left px-4 py-3 font-medium sticky left-0 bg-gray-50">Job</th>
                {analysis.map(w => (
                  <th key={w.weekStart} className="text-center px-3 py-3 font-medium whitespace-nowrap">{w.label}</th>
                ))}
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-50">
              {jobs.map(job => (
                <tr key={job.id} className="hover:bg-gray-50">
                  <td className="px-4 py-3 sticky left-0 bg-white">
                    <div className="font-medium text-gray-900">{job.jobNumber}</div>
                    <div className="text-xs text-gray-400 truncate max-w-[12rem]">{job.description?.slice(0, 40) ?? ''}</div>
                  </td>
                  {analysis.map(w => {
                    const count = w.byJob[job.id] ?? 0
                    return (
                      <td key={w.weekStart} className="px-3 py-3 text-center">
                        {count > 0 ? (
                          <span className={`inline-flex items-center justify-center w-8 h-8 rounded-full text-sm font-bold ${count >= 4 ? 'bg-blue-600 text-white' : count >= 2 ? 'bg-blue-200 text-blue-800' : 'bg-gray-100 text-gray-600'}`}>
                            {count}
                          </span>
                        ) : (
                          <span className="text-gray-200">—</span>
                        )}
                      </td>
                    )
                  })}
                </tr>
              ))}
              {jobs.length === 0 && (
                <tr><td colSpan={analysis.length + 1} className="px-4 py-8 text-center text-gray-400 text-sm">No roster allocations found. Build the week roster to see forecast data.</td></tr>
              )}
              {/* Total row */}
              <tr className="bg-gray-50 border-t-2 border-gray-200">
                <td className="px-4 py-3 sticky left-0 bg-gray-50 font-semibold text-gray-900">Total crew allocated</td>
                {analysis.map(w => (
                  <td key={w.weekStart} className="px-3 py-3 text-center font-bold text-gray-900">{w.allocatedCount}</td>
                ))}
              </tr>
              <tr className="bg-gray-50">
                <td className="px-4 py-3 sticky left-0 bg-gray-50 text-xs text-gray-500">Utilisation</td>
                {analysis.map(w => (
                  <td key={w.weekStart} className="px-3 py-3 text-center">
                    <span className={`text-xs font-medium ${w.utilisation >= 90 ? 'text-red-600' : w.utilisation >= 70 ? 'text-amber-600' : 'text-green-600'}`}>
                      {w.utilisation}%
                    </span>
                  </td>
                ))}
              </tr>
            </tbody>
          </table>
        </div>
      </div>
    </div>
  )
}

import { buildSTPPayEvent, type STPPayEvent } from '../utils/stpUtils'

// ─────────────────────────────────────────────────────────────────
// STP PHASE 2 — PAY EVENT MODAL
// Builds ATO-compliant payload, shows summary + per-employee breakdown,
// allows download as JSON and stub submission to intermediary.
// Wire employment hero / Xero credentials to enable live lodgement.
// ─────────────────────────────────────────────────────────────────
function STPModal({ timesheets, employees, onClose }: {
  timesheets: any[]; employees: any[]; onClose: () => void
}) {
  const b         = getDocumentBranding()
  const [step, setStep]     = useState<'review'|'declare'|'result'>('review')
  const [loading, setLoading] = useState(false)
  const [result,  setResult]  = useState<any>(null)
  const [validResult, setValidResult] = useState<any>(null)

  const event: STPPayEvent = buildSTPPayEvent({
    timesheets,
    employees,
    payerABN:       b.abn || '12 345 678 901',
    payerName:      b.companyName || 'MCB Civil Electrical Pty Ltd',
    generatedByName: 'TrafficProcure Platform',
  })

  const fmtAUD = (n: number) => `$${n.toFixed(2).replace(/\B(?=(\d{3})+(?!\d))/g, ',')}`

  const basis: Record<string, string> = {
    F:'Full time', P:'Part time', C:'Casual', L:'Labour hire', N:'Non-employee'
  }

  const API_URL = (import.meta as any).env?.VITE_API_URL ?? 'https://trafficprocure-api.dirlayo69.workers.dev'

  // Step 1 — validate against API
  const handleValidate = async () => {
    setLoading(true)
    try {
      const res  = await fetch(`${API_URL}/api/stp/validate`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(event),
      })
      const data = await res.json()
      setValidResult(data)
      if (data.valid) setStep('declare')
    } catch {
      setValidResult({ valid: false, errors: [{ field: 'network', message: 'Could not reach API — check connection' }], warnings: [] })
    }
    setLoading(false)
  }

  // Step 2 — lodge via Xero Payroll (STP Phase 2) + stub
  const handleSubmit = async () => {
    setLoading(true)
    let xeroPayRunId: string | undefined
    let stpViaXero  = false

    try {
      // Fetch Xero Payroll employees to match by name
      const empRes  = await fetch(`${API_URL}/api/accounting/payroll-employees`)
      const empData = await empRes.json() as any
      const xeroEmps: any[] = empData.employees ?? []

      // Map payees to Xero employee IDs by full name match
      const payslips = event.payees.flatMap((p: any) => {
        const xe = xeroEmps.find((e: any) =>
          `${e.FirstName ?? ''} ${e.LastName ?? ''}`.trim().toLowerCase() === p.employeeName.toLowerCase()
        )
        if (!xe?.EmployeeID) return []
        const ordRate = p.hoursOrdinary > 0 ? (p.grossEarnings / (p.hoursOrdinary + p.hoursOvertime * 1.5 || 1)) : 0
        return [{
          employeeId:    xe.EmployeeID,
          employeeName:  p.employeeName,
          ordinaryHours: p.hoursOrdinary,
          overtimeHours: p.hoursOvertime,
          ordinaryRate:  ordRate,
          overtimeRate:  ordRate * 1.5,
        }]
      })

      if (payslips.length > 0) {
        const xeroRes  = await fetch(`${API_URL}/api/accounting/push-payrun`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            payRunPeriodStartDate: event.payPeriodFrom,
            payRunPeriodEndDate:   event.payPeriodTo,
            paymentDate:           event.payPeriodTo,
            payslips,
          }),
        })
        const xeroData = await xeroRes.json() as any
        xeroPayRunId   = xeroData.payRunId
        stpViaXero     = xeroData.stpLodged === true
      }
    } catch (e) {
      console.warn('[STP] Xero payroll push skipped:', e)
    }

    // Always also submit to STP stub for audit trail
    try {
      const res  = await fetch(`${API_URL}/api/stp/submit`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(event),
      })
      const data = await res.json()
      setResult({ ...data, xeroPayRunId, stpLodgedViaXero: stpViaXero })
      setStep('result')
    } catch {
      setResult({ success: false, message: 'Network error — could not reach API', xeroPayRunId, stpLodgedViaXero: stpViaXero })
      setStep('result')
    }
    setLoading(false)
  }

  const downloadJSON = () => {
    const blob = new Blob([JSON.stringify(event, null, 2)], { type: 'application/json' })
    const url  = URL.createObjectURL(blob)
    const a    = document.createElement('a')
    a.href     = url
    a.download = `STP2_PayEvent_${event.payPeriodFrom}.json`
    a.click()
    URL.revokeObjectURL(url)
  }

  const tfnWarnings = validResult?.warnings ?? []
  const hardErrors  = validResult?.errors   ?? []

  return (
    <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-3xl max-h-[90vh] flex flex-col">

        {/* Header */}
        <div className="flex items-start justify-between px-6 py-4 border-b">
          <div>
            <div className="flex items-center gap-2 mb-0.5">
              <span className="text-xs font-bold bg-blue-700 text-white px-2 py-0.5 rounded">ATO</span>
              <h2 className="text-lg font-bold text-gray-900">STP Phase 2 Pay Event</h2>
            </div>
            <p className="text-xs text-gray-500">
              Period {event.payPeriodFrom} → {event.payPeriodTo} &nbsp;·&nbsp;
              {event.payeeCount} employee{event.payeeCount !== 1 ? 's' : ''} &nbsp;·&nbsp;
              Gross {fmtAUD(event.totalGross)}
            </p>
          </div>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600 mt-0.5"><X className="w-5 h-5" /></button>
        </div>

        <div className="overflow-y-auto flex-1 px-6 py-4 space-y-4">

          {step === 'review' && (
            <>
              {/* KPI row */}
              <div className="grid grid-cols-3 gap-3">
                {[
                  { label: 'Total gross', value: fmtAUD(event.totalGross), color: 'text-gray-900' },
                  { label: 'Total PAYG withholding', value: fmtAUD(event.totalPAYG), color: 'text-red-600' },
                  { label: 'Total super (11.5%)', value: fmtAUD(event.totalSuper), color: 'text-blue-700' },
                ].map(k => (
                  <div key={k.label} className="border border-gray-200 rounded-xl p-3">
                    <div className="text-xs text-gray-400 mb-1">{k.label}</div>
                    <div className={`text-xl font-bold ${k.color}`}>{k.value}</div>
                  </div>
                ))}
              </div>

              {/* Payee table */}
              <div className="border border-gray-200 rounded-xl overflow-hidden">
                <div className="bg-gray-50 px-4 py-2 border-b text-xs font-semibold text-gray-500 uppercase tracking-wide">Employee breakdown</div>
                <div className="overflow-x-auto">
                  <table className="w-full text-sm">
                    <thead>
                      <tr className="text-xs text-gray-400 border-b">
                        <th className="text-left px-3 py-2">Employee</th>
                        <th className="text-left px-3 py-2">Basis</th>
                        <th className="text-right px-3 py-2">Gross</th>
                        <th className="text-right px-3 py-2">PAYG</th>
                        <th className="text-right px-3 py-2">Super</th>
                        <th className="text-center px-3 py-2">TFN</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-gray-50">
                      {event.payees.map(p => (
                        <tr key={p.payeeId} className="hover:bg-gray-50">
                          <td className="px-3 py-2.5">
                            <div className="font-medium text-gray-900">{p.employeeName}</div>
                            <div className="text-xs text-gray-400">{p.classification} · {p.hoursOrdinary.toFixed(1)}h ord {p.hoursOvertime > 0 ? `+ ${p.hoursOvertime.toFixed(1)}h OT` : ''}</div>
                          </td>
                          <td className="px-3 py-2.5 text-xs text-gray-600">{basis[p.employmentBasis]}</td>
                          <td className="px-3 py-2.5 text-right font-semibold">{fmtAUD(p.grossEarnings)}</td>
                          <td className="px-3 py-2.5 text-right text-red-600">{fmtAUD(p.paygWithholding)}</td>
                          <td className="px-3 py-2.5 text-right text-blue-700">{fmtAUD(p.superGuarantee)}</td>
                          <td className="px-3 py-2.5 text-center">
                            {p.tfnPending
                              ? <span className="text-xs bg-amber-100 text-amber-700 px-1.5 py-0.5 rounded-full font-medium">Pending</span>
                              : <span className="text-xs bg-green-100 text-green-700 px-1.5 py-0.5 rounded-full font-medium">✓</span>
                            }
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>

              {/* Validation result */}
              {validResult && (
                <div className={`rounded-xl px-4 py-3 text-sm ${
                  validResult.valid ? 'bg-green-50 border border-green-200 text-green-800'
                  : 'bg-red-50 border border-red-200 text-red-800'}`}>
                  {validResult.valid
                    ? '✓ Payload validated — no structural errors. Ready to lodge.'
                    : hardErrors.map((e: any) => <div key={e.field}>{e.payeeId ? `[${e.payeeId}] ` : ''}{e.message}</div>)
                  }
                </div>
              )}
              {tfnWarnings.length > 0 && (
                <div className="bg-amber-50 border border-amber-200 rounded-xl px-4 py-3 text-xs text-amber-800 space-y-1">
                  <div className="font-semibold mb-1">⚠ TFN declarations pending ({tfnWarnings.length})</div>
                  {tfnWarnings.map((w: any, i: number) => <div key={i}>{w.message}</div>)}
                </div>
              )}

              {/* Xero Payroll notice */}
              <div className="bg-green-50 border border-green-200 rounded-xl px-4 py-3 text-xs text-green-800">
                <strong>STP lodgement via Xero Payroll</strong><br />
                When you lodge this pay event, TrafficProcure will:<br />
                1. Match employees to your Xero Payroll employees by name<br />
                2. Create a pay run in Xero Payroll for the period above<br />
                3. Post the pay run — Xero then lodges STP Phase 2 with the ATO automatically<br />
                <span className="text-green-600 font-medium">Ensure your employees are set up in Xero Payroll with matching names before lodging.</span>
              </div>
            </>
          )}

          {step === 'declare' && (
            <div className="space-y-4">
              <div className="bg-amber-50 border border-amber-200 rounded-xl px-5 py-4">
                <h3 className="font-semibold text-amber-900 mb-2">STP Declaration</h3>
                <p className="text-sm text-amber-800">
                  I declare that the information transmitted in this pay event is true and correct and
                  that I am authorised to make this declaration on behalf of <strong>{event.payerName}</strong> (ABN {event.payerABN}).
                  I understand the information will be transmitted to the Australian Taxation Office.
                </p>
                <p className="text-xs text-amber-700 mt-2">
                  Pay period: {event.payPeriodFrom} to {event.payPeriodTo} &nbsp;·&nbsp;
                  {event.payeeCount} employees &nbsp;·&nbsp;
                  Gross {fmtAUD(event.totalGross)} &nbsp;·&nbsp;
                  PAYG {fmtAUD(event.totalPAYG)} &nbsp;·&nbsp;
                  Super {fmtAUD(event.totalSuper)}
                </p>
              </div>
            </div>
          )}

          {step === 'result' && result && (
            <div className={`rounded-xl px-5 py-4 ${
              result.success ? 'bg-green-50 border border-green-200' : 'bg-red-50 border border-red-200'}`}>
              <h3 className={`font-semibold mb-2 ${ result.success ? 'text-green-900' : 'text-red-900'}`}>
                {result.success ? '✓ STP Pay Event Lodged' : '✗ Lodgement Failed'}
              </h3>
              <p className="text-sm text-gray-700">{result.message}</p>
              {result.submissionRef    && <p className="text-xs text-gray-500 mt-1">Ref: <code>{result.submissionRef}</code></p>}
              {result.atoReceiptId    && <p className="text-xs text-gray-500">ATO Receipt: <code>{result.atoReceiptId}</code></p>}
              {result.xeroPayRunId    && (
                <div className="mt-3 bg-green-100 border border-green-200 rounded-lg px-3 py-2 text-xs text-green-800">
                  <strong>✓ Xero Payroll pay run created</strong><br />
                  Pay Run ID: <code>{result.xeroPayRunId}</code><br />
                  {result.stpLodgedViaXero
                    ? 'Pay run posted — Xero will lodge STP Phase 2 with the ATO automatically.'
                    : 'Pay run created in draft — review and post in Xero Payroll to trigger STP lodgement.'}
                </div>
              )}
              {!result.xeroPayRunId && result.success && (
                <div className="mt-3 bg-amber-50 border border-amber-200 rounded-lg px-3 py-2 text-xs text-amber-800">
                  ⚠ No Xero Payroll employees matched — set up employees in Xero Payroll with matching names to enable automatic STP lodgement.
                </div>
              )}
            </div>
          )}
        </div>

        {/* Footer actions */}
        <div className="px-6 py-4 border-t bg-gray-50 flex items-center justify-between gap-3">
          <button onClick={downloadJSON} className="flex items-center gap-2 text-sm text-gray-600 hover:text-gray-900 border border-gray-300 bg-white rounded-lg px-3 py-2 font-medium hover:bg-gray-50 transition-colors">
            <Download className="w-4 h-4" />Download JSON
          </button>
          <div className="flex gap-2">
            <button onClick={onClose} className="btn-secondary text-sm">Close</button>
            {step === 'review' && (
              <button onClick={handleValidate} disabled={loading || event.payeeCount === 0}
                className="btn-primary text-sm disabled:opacity-50 flex items-center gap-2">
                {loading ? <><div className="w-3.5 h-3.5 border-2 border-white border-t-transparent rounded-full animate-spin" />Validating…</> : 'Validate payload'}
              </button>
            )}
            {step === 'declare' && (
              <button onClick={handleSubmit} disabled={loading}
                className="bg-blue-700 hover:bg-blue-800 text-white text-sm rounded-lg px-4 py-2 font-medium transition-colors flex items-center gap-2">
                {loading ? <><div className="w-3.5 h-3.5 border-2 border-white border-t-transparent rounded-full animate-spin" />Lodging…</> : 'I declare — lodge pay event'}
              </button>
            )}
            {step === 'result' && (
              <button onClick={() => setStep('review')} className="btn-secondary text-sm">New pay event</button>
            )}
          </div>
        </div>
      </div>
    </div>
  )
}

export function Timesheets() {
  const { user, isTier } = useAuthStore()
  const isPM     = isTier('B_PM')
  const isAdmin  = isTier('A_DEVELOPER')
  const [pageView, setPageView] = useState<'timesheets'|'reconcile'|'payslips'|'forecast'>('timesheets')
  const [showSTP, setShowSTP]   = useState(false)
    const [timesheets, setTimesheets] = useState<TS[]>(MOCK_TIMESHEETS as TS[])
  const [apiSeeded, setApiSeeded]   = useState(false)

  // Live timesheets from Neon — seeded into state on first load
  // IDs never clash (Neon UUIDs vs mock ts_1/ts_2 format)
  const { data: apiTimesheets = [], isLoading: tsLoading, isError: tsError } = useQuery({
    queryKey: ['timesheets'],
    queryFn:  () => fetchTimesheets(),
    staleTime: 60_000,
  })

  useEffect(() => {
    if (apiTimesheets.length > 0 && !apiSeeded) {
      const apiIds = new Set(apiTimesheets.map((t: any) => t.id))
      setTimesheets([
        ...(apiTimesheets as unknown as TS[]),
        ...(MOCK_TIMESHEETS as TS[]).filter((t) => !apiIds.has(t.id)),
      ])
      setApiSeeded(true)
    }
  }, [apiTimesheets, apiSeeded])
  const [view, setView]             = useState<'mine'|'team'|'costs'>('mine')

  // Employee selector — PM can switch between all employees
  // Default: own employee ID; for this prototype, assume current user = e1 (Tony Rivera)
  const currentEmployeeId = 'e1'  // in production: derive from user auth token
  const [selectedEmployeeId, setSelectedEmployeeId] = useState(currentEmployeeId)

  const isViewingOtherEmployee = selectedEmployeeId !== currentEmployeeId

  // Active timesheet = selected employee's (or own if no selection)
  const myTS   = timesheets.find(t => t.employeeId === selectedEmployeeId)
                ?? timesheets.find(t => t.employeeId === currentEmployeeId)
                ?? timesheets[0]
  const teamTS = timesheets.filter(t => t.employeeId !== currentEmployeeId)
  const myEmp  = MOCK_EMPLOYEES.find(e => e.id === myTS?.employeeId)
  const myRate = PAY_RATES[myEmp?.payClassification ?? ''] ?? 43

  const updateEntry = (tsId: string, entryId: string, field: string, val: any) => {
    const ts = timesheets.find(t => t.id === tsId)
    const isPMEditingSubmitted = isPM && ts?.status !== 'DRAFT'
    setTimesheets(prev => prev.map(t => t.id !== tsId ? t : {
      ...t,
      entries: t.entries.map(e => {
        if (e.id !== entryId) return e
        const updated = {
          ...e,
          [field]: val,
          ...(isPMEditingSubmitted ? { adjustedByPM: true, pmAdjustedAt: new Date().toISOString() } : {})
        }
        if (field === 'jobId') {
          const job = MOCK_JOBS.find(j => j.id === val)
          return { ...updated, jobNumber: job?.jobNumber ?? null }
        }
        return updated
      })
    }))
  }

  const submitTS = (id: string) => {
    setTimesheets(prev => prev.map(t => t.id === id ? { ...t, status: 'SUBMITTED', submittedAt: new Date().toISOString() } : t))
    updateTimesheetStatus(id, 'submit')
  }
  const approveTS = (id: string) => {
    const ts = timesheets.find(t => t.id === id)
    setTimesheets(prev => prev.map(t => t.id === id ? { ...t, status: 'APPROVED', approvedById: 'u2', approvedAt: new Date().toISOString() } : t))
    if (ts) fireTimesheetApprovedAlert(ts, user?.fullName ?? 'PM')
    updateTimesheetStatus(id, 'approve', { approvedById: user?.id ?? 'u2' })
  }
  const rejectTS = (id: string, reason: string) => {
    setTimesheets(prev => prev.map(t => t.id === id ? { ...t, status: 'REJECTED', rejectedReason: reason } : t))
    updateTimesheetStatus(id, 'reject', { pmNotes: reason })
  }

  const approvedForExport = timesheets.filter(t => t.status === 'APPROVED').length

  return (
    <div className="space-y-5">
      {tsLoading && (
        <div className="flex items-center gap-2 text-sm text-blue-700 bg-blue-50 border border-blue-100 rounded-xl px-4 py-3">
          <div className="w-3.5 h-3.5 border-2 border-blue-400 border-t-transparent rounded-full animate-spin flex-shrink-0" />
          Loading live timesheets from Neon...
        </div>
      )}
      {tsError && (
        <div className="text-sm text-amber-700 bg-amber-50 border border-amber-200 rounded-xl px-4 py-3">
          ⚠ API unreachable — showing local timesheet data only.
        </div>
      )}
      {apiSeeded && apiTimesheets.length > 0 && (
        <div className="text-sm text-green-700 bg-green-50 border border-green-100 rounded-xl px-4 py-3">
          ✓ {apiTimesheets.length} live timesheet{apiTimesheets.length !== 1 ? 's' : ''} loaded from Neon.
        </div>
      )}
      <div className="flex items-center justify-between gap-4">
        <div>
          <div className="flex items-center justify-between gap-3 flex-wrap">
        <h1 className="text-2xl font-bold text-gray-900">Timesheets</h1>
        <div className="flex gap-1 bg-gray-100 rounded-xl p-1">
          {([
            { key:'timesheets', label:'Timesheets' },
            { key:'reconcile',  label:'Reconciliation' },
            { key:'payslips',   label:'Payslips' },
            { key:'forecast',   label:'Resource forecast' },
          ] as const).map(v => (
            <button key={v.key} onClick={() => setPageView(v.key)}
              className={`text-sm font-medium px-4 py-1.5 rounded-lg transition-colors ${pageView === v.key ? 'bg-white text-gray-900 shadow-sm' : 'text-gray-500 hover:text-gray-700'}`}>
              {v.label}
            </button>
          ))}
        </div>
      </div>
          <p className="text-sm text-gray-500 mt-0.5">Week of {new Date(WEEK_DATES[0]).toLocaleDateString('en-AU', { day: 'numeric', month: 'short', year: 'numeric' })}</p>
        </div>
        <div className="flex gap-2 flex-wrap">
          <button onClick={() => generateLabourCostByJobReport(timesheets, MOCK_EMPLOYEES, PAY_RATES, MOCK_JOBS)} className="btn-secondary flex items-center gap-2 text-sm">
            <Download className="w-4 h-4" /> Labour cost by job
          </button>
          <button onClick={() => generateOvertimeAnalysisReport(timesheets, MOCK_EMPLOYEES, PAY_RATES)} className="btn-secondary flex items-center gap-2 text-sm">
            <Download className="w-4 h-4" /> Overtime analysis
          </button>
          {(isAdmin || isPM) && approvedForExport > 0 && (
            <>
              <button onClick={() => exportPayrollCSV(timesheets, MOCK_EMPLOYEES, PAY_RATES)} className="btn-secondary flex items-center gap-2 text-sm">
                <Download className="w-4 h-4" /> Export payroll ({approvedForExport})
              </button>
              <button onClick={() => exportABAFile(timesheets, MOCK_EMPLOYEES)} className="btn-secondary flex items-center gap-2 text-sm">
                <Download className="w-4 h-4" /> ABA bank file
              </button>
              <button onClick={() => setShowSTP(true)}
                className="flex items-center gap-2 text-sm bg-blue-700 hover:bg-blue-800 text-white px-3 py-2 rounded-lg font-medium transition-colors">
                <span className="text-xs font-bold bg-white/20 px-1 rounded">ATO</span> STP Phase 2
              </button>
            </>
          )}
        </div>
      </div>

      {/* PM employee switcher */}
      {isPM && (
        <div className="flex items-center gap-3 bg-white border border-gray-200 rounded-xl px-4 py-3">
          <UserCheck className="w-4 h-4 text-gray-400 flex-shrink-0" />
          <span className="text-sm text-gray-600 font-medium">Viewing timesheet for:</span>
          <select
            className="input text-sm flex-1"
            value={selectedEmployeeId}
            onChange={e => { setSelectedEmployeeId(e.target.value); setView('mine') }}
          >
            <option value={currentEmployeeId}>
              {MOCK_EMPLOYEES.find(e => e.id === currentEmployeeId)?.fullName ?? 'Me'} (my timesheet)
            </option>
            {timesheets
              .filter(t => t.employeeId !== currentEmployeeId)
              .map(t => {
                const emp = MOCK_EMPLOYEES.find(e => e.id === t.employeeId)
                const tot = totalHours(t)
                return (
                  <option key={t.employeeId} value={t.employeeId}>
                    {t.employeeName} — {t.status} · {tot.total.toFixed(1)}h
                    {t.entries.some(e => e.adjustedByPM) ? ' ✏ adj.' : ''}
                  </option>
                )
              })
            }
          </select>
          {isViewingOtherEmployee && (
            <button onClick={() => setSelectedEmployeeId(currentEmployeeId)}
              className="text-xs text-gray-400 hover:text-gray-600 flex items-center gap-1">
              <RotateCcw className="w-3.5 h-3.5" />Reset
            </button>
          )}
        </div>
      )}

      {/* View tabs */}
      <div className="flex gap-1 bg-gray-100 rounded-xl p-1 w-fit">
        <button onClick={() => setView('mine')} className={`text-sm font-medium px-5 py-2 rounded-lg transition-colors flex items-center gap-2 ${view === 'mine' ? 'bg-white text-gray-900 shadow-sm' : 'text-gray-500 hover:text-gray-700'}`}><ClipboardList className="w-4 h-4" />My timesheet</button>
        {isPM && <button onClick={() => setView('team')} className={`text-sm font-medium px-5 py-2 rounded-lg transition-colors flex items-center gap-2 ${view === 'team' ? 'bg-white text-gray-900 shadow-sm' : 'text-gray-500 hover:text-gray-700'}`}><Users className="w-4 h-4" />Team ({teamTS.length})</button>}
        {isPM && <button onClick={() => setView('costs')} className={`text-sm font-medium px-5 py-2 rounded-lg transition-colors flex items-center gap-2 ${view === 'costs' ? 'bg-white text-gray-900 shadow-sm' : 'text-gray-500 hover:text-gray-700'}`}><DollarSign className="w-4 h-4" />Labour costs</button>}
      </div>

      {view === 'mine' && myTS && (
        <MyTimesheet
          ts={myTS}
          payRate={myRate}
          isPMEdit={isViewingOtherEmployee && isPM}
          viewingEmployeeName={isViewingOtherEmployee ? myEmp?.fullName : undefined}
          onSubmit={() => submitTS(myTS.id)}
          onUpdate={(entryId, field, val) => updateEntry(myTS.id, entryId, field, val)}
        />
      )}
      {view === 'team'  && isPM && (
        <TeamTimesheets
          timesheets={teamTS}
          onApprove={(tsId: string) => {
            const ts = teamTS.find(t => t.id === tsId)
            if (ts) { // electToil removed from param
              const h = totalHours(ts)
              const otHrs = h.overtime + h.doubleTime
              if (otHrs > 0) {
                const emp = MOCK_EMPLOYEES.find((e: any) => e.id === ts.employeeId)
                accrueToil(ts.employeeId, otHrs, ts.weekStarting, user?.fullName ?? 'PM')
                console.log(`TOIL: ${otHrs}h accrued for ${emp?.fullName} — period ${ts.weekStarting}`)
              }
            }
            approveTS(tsId)
          }}
          onReject={rejectTS}
          onSelectEmployee={id => { setSelectedEmployeeId(id); setView('mine') }}
          selectedEmployeeId={selectedEmployeeId}
        />
      )}
      {view === 'costs' && isPM && <LabourCostSummary timesheets={timesheets} />}
      {pageView === 'reconcile' && <ReconciliationTab />}
      {pageView === 'forecast'  && <ResourceForecastTab />}
      {pageView === 'payslips' && (
        <div className="space-y-4 mt-4">
          <div className="bg-white border border-gray-200 rounded-xl p-5">
            <div className="flex items-center justify-between mb-4">
              <div>
                <h3 className="font-semibold text-gray-900">Generate payslips</h3>
                <p className="text-sm text-gray-500 mt-0.5">Payslips use company branding from Settings. Downloads as HTML for printing or PDF.</p>
              </div>
              {isPM && (
                <button onClick={() => generatePLSLReport(timesheets, MOCK_EMPLOYEES, PAY_RATES)}
                  className="flex items-center gap-1.5 text-xs bg-purple-600 hover:bg-purple-700 text-white px-3 py-1.5 rounded-lg font-medium transition-colors">
                  <Download className="w-3 h-3" />CIPQ/BEWT levy report
                </button>
              )}
            </div>
            <div className="space-y-2">
              {MOCK_TIMESHEETS.filter((ts: any) => ts.status === 'APPROVED' || ts.status === 'PROCESSED').map((ts: any) => {
                const emp  = MOCK_EMPLOYEES.find((e: any) => e.id === ts.employeeId) as any
                const rate = getEffectiveRate(emp?.payClassification ?? '', (emp as any)?.isAdultApprentice ?? false)
                const gross = estimatedGross(ts, rate)
                return (
                  <div key={ts.id} className="flex items-center gap-4 px-4 py-3 border border-gray-200 rounded-xl hover:bg-gray-50">
                    <div className="flex-1">
                      <div className="font-medium text-sm text-gray-900">{ts.employeeName}</div>
                      <div className="text-xs text-gray-400">Week {ts.weekStarting} · Est. gross {formatCurrency(gross)} · {ts.status}</div>
                    </div>
                    <button onClick={() => generatePayslip(ts, rate)}
                      className="flex items-center gap-1.5 text-xs bg-blue-600 hover:bg-blue-700 text-white px-3 py-1.5 rounded-lg font-medium transition-colors">
                      <Download className="w-3 h-3" />Download payslip
                    </button>
                  </div>
                )
              })}
              {MOCK_TIMESHEETS.filter((ts: any) => ts.status === 'APPROVED' || ts.status === 'PROCESSED').length === 0 && (
                <p className="text-center py-8 text-gray-400 text-sm">No approved timesheets yet. Approve timesheets to generate payslips.</p>
              )}
            </div>
          </div>
        </div>
      )}

      {/* STP Phase 2 modal */}
      {showSTP && (
        <STPModal
          timesheets={timesheets}
          employees={MOCK_EMPLOYEES}
          onClose={() => setShowSTP(false)}
        />
      )}
    </div>
  )
}
